import { ensureFreshSession } from "@/lib/auth-refresh";
import {
  CHAT_GAP_OVERLAP_MS,
  INBOX_OPEN_CHAT_SYNC_DELAY_MS,
  hasMessageAtInstant,
  maxServerTimestamp,
  sinceWithOverlap,
} from "@/lib/inbox-live-sync";
import {
  CHAT_MESSAGE_GAP_LIMIT,
  CHAT_MESSAGE_PAGE_SIZE,
  fetchChatMessageRows,
} from "@/lib/fetch-chat-messages";
import {
  CHAT_ACTIVITY_WINDOW_LIMIT,
  mergeActivityLogs,
  shouldRefetchChatOnFocus,
  type ActivityWindow,
} from "@/lib/chat-activity-window";
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Bot, User, Image, Mic, X, Paperclip, Play, Pause, Phone, Instagram, CheckCheck, Smile, FileText, Reply, UserPlus, UserRound, CheckCircle, RotateCcw, ExternalLink, Sparkles, RefreshCw, Check, AlertCircle, Trash2, Square, ChevronDown, MoreVertical, LayoutTemplate, LayoutList, Clock, Ban, ShoppingBag, Store, Smartphone, Pencil, ListTodo, Plus, Camera, Contact, Forward } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { LayoutsPopover, type LayoutsPopoverHandle } from "@/components/chat/LayoutsPopover";
import { ChatProductLookupDialog } from "@/components/chat/ChatProductLookupDialog";
import { AttachMessageToTaskDialog } from "@/components/tasks/AttachMessageToTaskDialog";
import { ExpandableMessageText } from "@/components/chat/ExpandableMessageText";
import { ChatMessageTextRow } from "@/components/chat/ChatMessageTextRow";
import { MessageTimestampMeta } from "@/components/chat/MessageTimestampMeta";
import { SendWhatsAppTemplateDialog } from "@/components/chat/SendWhatsAppTemplateDialog";
import { SendWhatsAppInteractiveDialog } from "@/components/chat/SendWhatsAppInteractiveDialog";
import { SendWhatsAppContactDialog } from "@/components/chat/SendWhatsAppContactDialog";
import { ForwardMessageDialog } from "@/components/chat/ForwardMessageDialog";
import {
  WhatsAppCarouselMessageBubble,
  WhatsAppInteractiveMessageBubble,
  WhatsAppInteractiveReplyBubble,
  WhatsAppPollMessageBubble,
} from "@/components/chat/WhatsAppInteractiveMessageBubble";
import { WhatsAppCatalogOrderBubble } from "@/components/chat/WhatsAppCatalogOrderBubble";
import { SharedContactBubble } from "@/components/chat/SharedContactBubble";
import { AdReplyCard } from "@/components/chat/AdReplyCard";
import { parseCatalogOrderDisplay } from "@/lib/whatsapp-catalog-order";
import {
  isInteractivePlaceholderContent,
  isUnsupportedInboundPoll,
  parseFabPoll,
  parseInboundInteractiveReply,
  parseInteractivePreviewFromBody,
  parseOutboundCarouselDisplay,
  parseOutboundInteractive,
  parseOutboundPollDisplay,
  stripInteractivePreviewFallback,
} from "@/lib/whatsapp-interactive-message";
import {
  getWhatsAppProviderCapabilities,
  supportsInteractiveOutbound,
  type WhatsAppProviderId,
} from "@/lib/whatsapp-provider-capabilities";
import {
  isImagePlaceholderContent,
  isSharePlaceholderContent,
  isVideoPlaceholderContent,
  resolveMediaCaption,
  resolveShareCaption,
} from "@/lib/chat-message-caption";
import { getMessageTextLayout } from "@/lib/chat-message-layout";
import { canForwardWhatsAppMessage } from "@/lib/whatsapp-forward-message";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { DocumentMessageBubble } from "@/components/chat/DocumentMessageBubble";
import {
  subscribePostgresChannelResilient,
  type ResilientChannelHandle,
} from "@/lib/realtime-channel";
import { useWorkspaceConversationRealtime } from "@/hooks/useWorkspaceConversationRealtime";
import {
  REALTIME_PAUSE_ON_HIDDEN_ENABLED,
  type ConversationChangeHandler,
} from "@/lib/workspace-conversation-realtime-hub";

import { format, isToday, isYesterday, isSameDay, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  formatChatActivityLogTime,
  formatChatDateSeparator,
  shouldShowChatDateSeparator,
} from "@/lib/chat-date-format";
import { normalizeReactionEmoji } from "@/lib/reaction-emoji";
import { createOpusRecorder, audioBlobExtension, type OpusRecorderHandle } from "@/lib/opus-audio-recorder";



import { cn, formatWhatsAppMarkdown } from "@/lib/utils";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import { realtimeChannelsCountDelta } from "@/lib/frontend-telemetry";
import {
  mergeEnrichmentIntoChannelMessages,
  mergeInitialMessagesLoad,
  mergeMessagesByChannel,
  sortMessagesByChannel,
  sortMessagesChronologically,
} from "@/lib/message-merge";
import {
  cancelBackgroundRefresh,
  dedupeMessageFetch,
  getChatMessageCache,
  invalidateChatMessageCacheForConversation,
  isChatMessageCacheStale,
  makeChatMessageCacheKey,
  markChatMessageCacheStale,
  setChatMessageCache,
} from "@/lib/chat-message-cache";
import {
  CHAT_SYNC_MESSAGE_SELECT,
  buildHubPreviewMessageId,
  collectMemberIdsFromMessages,
  collectStaleOutboundMessageIds,
  computeLastMessageAt,
  fetchMessageEnrichment,
  groupRowsByChannel,
  hasStaleOutboundMessages,
  HUB_PREVIEW_MESSAGE_SOURCE,
  hubPreviewMatchesOutboundBody,
  isHubPreviewMessage,
  isLegacyInstagramReactionMessage,
  removeSupersededHubPreviews,
  mergeMessageMetadata,
  patchMessageFromServerRow,
  serverRowToChatMessage,
  type ServerMessageRow,
} from "@/lib/chat-message-state";
import { Skeleton } from "@/components/ui/skeleton";

async function invokeWithRetry(fnName: string, body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke(fnName, { body });
  if (error) {
    const msg = (error.message || "").toLowerCase();
    const isSessionError =
      msg.includes("failed to send") ||
      msg.includes("jwt") ||
      msg.includes("401") ||
      msg.includes("networkerror");
    if (isSessionError) {
      await ensureFreshSession(true);
      return supabase.functions.invoke(fnName, { body });
    }
  }
  return { data, error };
}

async function hydrateOutboundFromSendResponse(
  sendData: Record<string, unknown> | null | undefined,
  conversationId: string,
  applyRows: (rows: ServerMessageRow[]) => void,
) {
  if (!sendData || sendData.success === false) return;

  const snapshot = sendData.message as ServerMessageRow | undefined;
  if (snapshot?.id) {
    invalidateChatMessageCacheForConversation(conversationId);
    applyRows([{ ...snapshot, conversation_id: conversationId }]);
    return;
  }

  const existingId = sendData.existingId as string | undefined;
  if (sendData.deduped && existingId) {
    const { data } = await supabase
      .from("messages")
      .select(CHAT_SYNC_MESSAGE_SELECT)
      .eq("id", existingId)
      .maybeSingle();
    if (data) {
      invalidateChatMessageCacheForConversation(conversationId);
      applyRows([data as ServerMessageRow]);
    }
  }
}
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/hooks/useTheme";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import EmojiPicker, { EmojiClickData, Theme as EmojiPickerTheme } from 'emoji-picker-react';

interface MessageReaction {
  reaction: string;
  from_user: string;
}

type OutgoingMessageMeta = {
  source?: string;
  automated?: boolean;
  catalog_sent?: boolean;
  whatsapp_key?: { fromMe?: boolean };
  is_echo?: boolean;
  sent_from?: string;
  agent_id?: string;
  agent_name?: string;
};

function getOutgoingMeta(message: Message): OutgoingMessageMeta | undefined {
  return message.metadata as OutgoingMessageMeta | undefined;
}

/** Mensagem enviada pelo celular/app nativo (WhatsApp ou Instagram), não pela IA nem pelo painel. */
function isOutgoingFromManualPhone(message: Message): boolean {
  if (message.role !== "user") return false;
  if (message.sent_by_member_id || message.sent_by_member?.display_name) return false;

  const meta = getOutgoingMeta(message);
  if (meta?.source === "whatsapp_manual_phone") return true;
  if (meta?.source === "instagram_manual") return true;
  if (meta?.is_echo === true && meta?.sent_from === "mobile_app") return true;

  if (
    meta?.whatsapp_key?.fromMe === true &&
    meta?.source !== "ai" &&
    meta?.source !== "followup" &&
    meta?.catalog_sent !== true &&
    meta?.automated !== true
  ) {
    return true;
  }

  return false;
}

function isOutgoingFromAi(message: Message): boolean {
  if (message.role !== "user") return false;
  if (message.sent_by_member_id || message.sent_by_member?.display_name) return false;
  if (isOutgoingFromManualPhone(message)) return false;

  const meta = getOutgoingMeta(message);
  if (meta?.source === "ai" || meta?.source === "followup") return true;
  if (meta?.automated === true) return true;
  if (meta?.catalog_sent === true) return true;

  // Compat: saídas antigas da IA sem metadata explícito
  return true;
}

function getManualPhoneSenderLabel(): string {
  return "Celular";
}

function isFollowupAiMessage(message: Message): boolean {
  const meta = message.metadata as { source?: string; step_key?: string } | undefined;
  if (meta?.source === "followup") return true;
  const stepKey = meta?.step_key;
  return typeof stepKey === "string" && stepKey.startsWith("followup_");
}

function getAiSenderLabel(message: Message): string {
  const meta = message.metadata as OutgoingMessageMeta | undefined;
  const agentName =
    typeof meta?.agent_name === "string" && meta.agent_name.trim()
      ? meta.agent_name.trim()
      : "IA";
  return isFollowupAiMessage(message) ? `${agentName} · follow-up` : agentName;
}

interface SentByMember {
  display_name: string | null;
}

interface Message {
  id?: string;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
  provider_message_id?: string | null;
  metadata?: {
    type?: string;
    message_id?: string;
    media?: {
      base64?: string;
      mimetype?: string;
      url?: string;
      ptt?: boolean;
      fileName?: string;
      objectKey?: string;
      object_key?: string;
      storageProvider?: string;
      storage_provider?: string;
      pending?: boolean;
      status?: string;
    };
    quoted_message_id?: string;
    quoted_message_body?: string;
    quoted_message_from?: string;
    is_story_reply?: boolean;
    story_url?: string;
    story_id?: string;
    source?: string;
    agent_id?: string;
    agent_name?: string;
    catalog_sent?: boolean;
    automated?: boolean;
    whatsapp_key?: { fromMe?: boolean; remoteJid?: string; id?: string };
    // Semantic fields from webhook normalization
    normalized_event_type?: string;
    normalized_message_subtype?: string;
    emoji_only?: boolean;
    agent_eligible?: boolean;
    is_story_context?: boolean;
    failure_reason?: string;
    friendly_message?: string;
    error_code?: number | null;
    transcription?: string;
    audio_transcription?: string;
    // Catalog product inquiry (lead sent a product from catalog)
    product?: {
      id?: string;
      title?: string;
      price?: string | number;
      currency?: string;
      imageUrl?: string;
      sellerJid?: string;
      description?: string;
      url?: string;
    };
    // Quoted/replied message context (v2 — supports product/text/media)
    quoted?: {
      kind?: 'product' | 'text' | 'media';
      messageId?: string;
      text?: string;
      mediaType?: string;
      caption?: string;
      product?: {
        id?: string;
        title?: string;
        price?: string | number;
        currency?: string;
        imageUrl?: string;
        sellerJid?: string;
        description?: string;
        url?: string;
      };
    };
  };
  reactions?: MessageReaction[];
  sent_by_member?: SentByMember | null;
  sent_by_member_id?: string | null;
  delivery_status?: string | null;
  deleted_at?: string | null;
  deleted_by_member_id?: string | null;
  deleted_scope?: string | null;
  deleted_by_member?: SentByMember | null;
}

interface ActivityLog {
  id: string;
  action_type: string;
  created_at: string;
  metadata?: any;
  member: {
    display_name: string | null;
  } | null;
}

interface ChatInterfaceProps {
  workspaceId: string;
  leadId: string;
  leadName: string;
  leadMetadata?: any;
  conversations: Array<{
    channel: string;
    conversation_id: string;
    state: string;
    followup_enabled: boolean;
    integration_id?: string | null;
    originLabel?: string | null;
  }>;
  onPauseConversation: (conversationId: string) => Promise<void>;
  onResumeConversation: (conversationId: string) => Promise<void>;
  onCloseConversation: (conversationId: string) => Promise<void>;
  onToggleFollowup: (conversationId: string, currentStatus: boolean) => Promise<void>;
  onClearContext: (conversationId: string) => Promise<void>;
  clearMessagesSignal?: number;
  insertComposerText?: { nonce: number; text: string } | null;
  onLeadRecovery?: (conversationId: string) => void;
  isLeadRecovering?: boolean;
  leadRecoveryPlanAllowed?: boolean;
}

const CHANNEL_ICONS = {
  whatsapp: Phone,
  instagram: Instagram,
};

const IMAGE_ATTACHMENT_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif"];

function isImageAttachmentFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  const ext = file.name.split(".").pop()?.toLowerCase();
  return !!ext && IMAGE_ATTACHMENT_EXTENSIONS.includes(ext);
}

function getClipboardImageFiles(clipboard: DataTransfer): File[] {
  const fromFiles = Array.from(clipboard.files).filter(isImageAttachmentFile);
  if (fromFiles.length > 0) return fromFiles;

  if (!clipboard.items?.length) return [];

  const fromItems: File[] = [];
  for (const item of clipboard.items) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (file) fromItems.push(file);
  }
  return fromItems;
}

function dataTransferHasImages(dataTransfer: DataTransfer): boolean {
  if (dataTransfer.items?.length) {
    return Array.from(dataTransfer.items).some(
      (item) => item.kind === "file" && (item.type.startsWith("image/") || item.type === "")
    );
  }
  return dataTransfer.types.includes("Files");
}

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

function ChatMessagesSkeleton() {
  return (
    <div className="space-y-4 py-2" aria-busy="true" aria-label="Carregando mensagens">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn("flex gap-2", i % 2 === 0 ? "justify-start" : "justify-end")}
        >
          <Skeleton className={cn("h-10 rounded-2xl", i % 2 === 0 ? "w-[55%]" : "w-[45%]")} />
        </div>
      ))}
    </div>
  );
}

// Detectar mimetype correto de áudio pelos magic bytes do base64
const detectAudioMimetypeFromBase64 = (base64: string): string | null => {
  try {
    const binaryString = atob(base64.substring(0, 20));
    // OGG: "OggS"
    if (binaryString.startsWith('OggS')) return 'audio/ogg';
    // MP3: ID3 ou sync bytes
    if (binaryString.startsWith('ID3') || binaryString.charCodeAt(0) === 0xFF) return 'audio/mpeg';
    // WAV: "RIFF"
    if (binaryString.startsWith('RIFF')) return 'audio/wav';
    return null;
  } catch {
    return null;
  }
};

const AUDIO_PTT_BUILD = "ptt-ogg-only-2026-06-03";

const isWhatsAppPttAudioFile = async (file: File): Promise<boolean> => {
  const mime = (file.type || "").toLowerCase();
  const name = file.name.toLowerCase();
  if (
    mime.includes("ogg") || mime.includes("opus") ||
    mime.includes("wav") ||
    mime.includes("mp4") || mime.includes("m4a") || mime.includes("aac") ||
    mime.includes("mpeg") || mime.includes("mp3") ||
    mime.includes("webm") ||
    name.endsWith(".ogg") || name.endsWith(".oga") || name.endsWith(".opus") ||
    name.endsWith(".wav") || name.endsWith(".mp4") || name.endsWith(".m4a") ||
    name.endsWith(".aac") || name.endsWith(".mp3") || name.endsWith(".webm")
  ) {
    return true;
  }
  try {
    const header = await file.slice(0, 4).text();
    return header === "OggS" || header === "RIFF" || header.startsWith("ID3");
  } catch {
    return false;
  }
};

function getMediaPendingLabel(message: Message, outboundLabel: string): string {
  const meta = message.metadata as { source?: string; type?: string } | undefined;
  if (meta?.source === "whatsapp_cloud" || meta?.source === "whatsapp_uazapi") {
    switch (meta.type) {
      case "audio":
        return "Baixando áudio...";
      case "video":
        return "Baixando vídeo...";
      case "document":
        return "Baixando documento...";
      case "sticker":
        return "Baixando sticker...";
      case "image":
        return "Baixando imagem...";
      default:
        return "Baixando mídia...";
    }
  }
  return outboundLabel;
}

const AUDIO_TRANSCRIPTION_PLACEHOLDERS = new Set([
  "[Audio]",
  "[Áudio]",
  "[Audio nao transcrito]",
  "[Áudio não transcrito]",
  "[Áudio recebido]",
]);

function getAudioTranscription(message: Message): string | null {
  if (message.metadata?.type !== "audio") return null;

  const meta = message.metadata;
  const fromMeta =
    meta.transcription?.trim() || meta.audio_transcription?.trim() || null;
  if (fromMeta) return fromMeta;

  const body = message.content?.trim();
  if (body && !AUDIO_TRANSCRIPTION_PLACEHOLDERS.has(body)) return body;

  return null;
}

function formatAgentTransferLabel(meta: Record<string, unknown> | null | undefined): string {
  const sourceName =
    typeof meta?.source_agent_name === "string" && meta.source_agent_name.trim()
      ? meta.source_agent_name.trim()
      : null;
  const targetName =
    typeof meta?.target_agent_name === "string" && meta.target_agent_name.trim()
      ? meta.target_agent_name.trim()
      : null;

  if (sourceName && targetName) {
    return `Agente ${sourceName} transferiu atendimento para agente ${targetName}`;
  }
  if (targetName) {
    return `Atendimento transferido para agente ${targetName}`;
  }
  if (sourceName) {
    return `Agente ${sourceName} transferiu o atendimento`;
  }
  return "Atendimento transferido entre agentes";
}

export const ChatInterface = ({
  workspaceId,
  leadId,
  leadName,
  leadMetadata,
  conversations,
  onPauseConversation,
  onResumeConversation,
  onCloseConversation,
  onToggleFollowup,
  onClearContext,
  clearMessagesSignal = 0,
  insertComposerText = null,
  onLeadRecovery,
  isLeadRecovering = false,
  leadRecoveryPlanAllowed = false,
}: ChatInterfaceProps) => {
  const [activeChannel, setActiveChannel] = useState<string>(conversations[0]?.channel || 'whatsapp');
  const [messagesByChannel, setMessagesByChannel] = useState<{ [channel: string]: Message[] }>({});
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [oldestMessageAt, setOldestMessageAt] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [audioPlayers, setAudioPlayers] = useState<{ [key: number]: { playing: boolean; currentTime: number; duration: number; playbackRate: number } }>({});
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'preview'>('idle');
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [accountProfilePicture, setAccountProfilePicture] = useState<string | null>(null);
  const [accountOriginLabel, setAccountOriginLabel] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [productLookupOpen, setProductLookupOpen] = useState(false);
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState<number | null>(null);
  const [reactionPickerIndex, setReactionPickerIndex] = useState<number | null>(null);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState<number | null>(null);
  const [quotedMessage, setQuotedMessage] = useState<{
    id: string;
    messageId: string;
    content: string;
    from: string;
  } | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [editingMessage, setEditingMessage] = useState<{ id: string; body: string } | null>(null);
  const [attachToTaskMessage, setAttachToTaskMessage] = useState<Message | null>(null);
  const [editingText, setEditingText] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [interactiveDialogOpen, setInteractiveDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  const [cloudIntegrationIds, setCloudIntegrationIds] = useState<Set<string>>(new Set());
  const [integrationProviders, setIntegrationProviders] = useState<Record<string, WhatsAppProviderId>>({});
  const [cloudWindowExpiresAt, setCloudWindowExpiresAt] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [audioTranscriptionExpandedIds, setAudioTranscriptionExpandedIds] = useState<Set<string>>(new Set());
  const [enhancedText, setEnhancedText] = useState<string | null>(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement }>({});
  const opusRecorderRef = useRef<OpusRecorderHandle | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const stopIntentionRef = useRef<'preview' | 'discard'>('preview');
  const realtimeHandleRef = useRef<ResilientChannelHandle | null>(null);
  const lastMessageAtRef = useRef<string | null>(null);
  /** Maior created_at visto em linhas do servidor (busca/realtime). Envio local não conta. */
  const serverWatermarkRef = useRef<string | null>(null);
  const noteServerRows = (rows: ReadonlyArray<{ created_at?: string | null } | null | undefined>) => {
    serverWatermarkRef.current = maxServerTimestamp(
      serverWatermarkRef.current,
      rows.map((row) => row?.created_at ?? null),
    );
  };
  const syncNewMessagesRef = useRef<(() => Promise<void>) | null>(null);
  const requestSyncNewMessagesRef = useRef<((conversationLastMessageAt: string) => void) | null>(null);
  const fetchActivityWindowRef = useRef<
    ((bounds: ActivityWindow & { append?: boolean }) => Promise<void>) | null
  >(null);
  const filledExtraPageRef = useRef(false);
  const applyHubOutboundPreviewRef = useRef<
    ((conversationId: string, preview: string, lastMessageAt: string) => void) | null
  >(null);
  const reconcilePendingMessagesRef = useRef<(() => Promise<void>) | null>(null);
  const reconcileChatRef = useRef<(() => Promise<void>) | null>(null);
  const applyServerMessagesRef = useRef<((rows: ServerMessageRow[]) => void) | null>(null);
  const messagesByChannelRef = useRef(messagesByChannel);
  const loadGenerationRef = useRef(0);
  const prevConversationIdsRef = useRef<string[]>([]);
  const knownMessageIdsRef = useRef<Set<string>>(new Set());
  const openConversationIdsRef = useRef<string[]>([]);
  const refreshingMediaIdsRef = useRef<Set<string>>(new Set());

  const activityLogsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreAfterLoadOlderRef = useRef<{ prevScrollTop: number; prevScrollHeight: number } | null>(null);
  const pendingInitialScrollRef = useRef(true);
  const stickToBottomRef = useRef(true);
  const initialScrollResizeObserverRef = useRef<ResizeObserver | null>(null);
  const [messagesViewportReady, setMessagesViewportReady] = useState(false);
  const { toast } = useToast();
  const { memberId, memberDisplayName } = useMemberPermissions();
  const isMobile = useIsMobile();
  const { theme: appTheme } = useTheme();
  const emojiPickerTheme =
    appTheme === "dark"
      ? EmojiPickerTheme.DARK
      : appTheme === "light"
        ? EmojiPickerTheme.LIGHT
        : EmojiPickerTheme.AUTO;

  const refreshMinioMediaUrl = useCallback(async (message: Message): Promise<string | null> => {
    if (!message.id || refreshingMediaIdsRef.current.has(message.id)) return null;

    const media = message.metadata?.media as Record<string, unknown> | undefined;
    const currentUrl = typeof media?.url === "string" ? media.url : "";
    const hasMinioPointer =
      media?.storageProvider === "minio" ||
      media?.storage_provider === "minio" ||
      typeof media?.objectKey === "string" ||
      typeof media?.object_key === "string" ||
      currentUrl.includes("/inbound/") ||
      currentUrl.includes("/outbound/") ||
      currentUrl.includes("/cloud/");

    if (!hasMinioPointer) return null;

    refreshingMediaIdsRef.current.add(message.id);
    try {
      const { data, error } = await supabase.functions.invoke("presign-media-download", {
        body: { message_id: message.id },
      });
      if (error || !data?.download_url) throw error || new Error("download_url ausente");

      const nextUrl = String(data.download_url);
      setMessagesByChannel((prev) => {
        const next = { ...prev };
        for (const channel of Object.keys(next)) {
          next[channel] = next[channel].map((item) => {
            if (item.id !== message.id) return item;
            const currentMetadata = item.metadata ?? {};
            const currentMedia = (currentMetadata.media ?? {}) as NonNullable<Message["metadata"]>["media"];
            return {
              ...item,
              metadata: {
                ...currentMetadata,
                media_pending: false,
                media: {
                  ...currentMedia,
                  url: nextUrl,
                  objectKey: data.object_key ?? currentMedia.objectKey,
                  storageProvider: "minio",
                  pending: false,
                  status: "ready",
                },
              },
            };
          });
        }
        return next;
      });
      return nextUrl;
    } catch (error) {
 console.warn("Não foi possível renovar URL da mídia MinIO:", error);
      return null;
    } finally {
      refreshingMediaIdsRef.current.delete(message.id);
    }
  }, []);
  const [mobileEmojiDialogOpen, setMobileEmojiDialogOpen] = useState(false);
  const [mobileEmojiPickerHeight, setMobileEmojiPickerHeight] = useState(280);

  useEffect(() => {
    if (!mobileEmojiDialogOpen || typeof window === "undefined") return;
    const updateHeight = () => {
      const available = window.innerHeight * 0.42;
      setMobileEmojiPickerHeight(Math.min(300, Math.max(220, Math.round(available))));
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [mobileEmojiDialogOpen]);

  const layoutsPopoverRef = useRef<LayoutsPopoverHandle>(null);
  const mobileComposerTextareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const desktopComposerTextareaRef = useRef<HTMLTextAreaElement>(null);
  /** Largura do form do compositor: abaixo disso usamos menu ⋮ + campo largo; acima, barra completa de ícones. */
  const composerFormMeasureRef = useRef<HTMLFormElement>(null);
  const [useWideComposerToolbar, setUseWideComposerToolbar] = useState(false);
  const [isChatDragOver, setIsChatDragOver] = useState(false);
  const chatDragDepthRef = useRef(0);
  const sendingRef = useRef(false);

  type QueuedTextMessage = {
    conversationId: string;
    channel: string;
    text: string;
    clientMessageId: string;
    quotedMessage: {
      id: string;
      messageId: string;
      content: string;
      from: string;
    } | null;
    integrationId?: string | null;
  };
  const sendQueueRef = useRef<Map<string, QueuedTextMessage[]>>(new Map());
  const sendQueueProcessingRef = useRef<Set<string>>(new Set());
  const [activePendingSendCount, setActivePendingSendCount] = useState(0);

  const syncActivePendingSendCount = useCallback(() => {
    const convId = conversations.find((c) => c.channel === activeChannel)?.conversation_id;
    if (!convId) {
      setActivePendingSendCount(0);
      return;
    }
    const queued = sendQueueRef.current.get(convId)?.length ?? 0;
    const inFlight = sendQueueProcessingRef.current.has(convId) ? 1 : 0;
    setActivePendingSendCount(queued + inFlight);
  }, [conversations, activeChannel]);

  useEffect(() => {
    syncActivePendingSendCount();
  }, [activeChannel, syncActivePendingSendCount]);

  const COMPOSER_MAX_LINES = 3;
  /** Largura mínima (px) para caber ícones + textarea + “Enviar” sem esmagar o campo (medido no form). */
  const COMPOSER_TOOLBAR_WIDE_MIN_PX = 680;
  const toggleAudioTranscription = useCallback((messageId: string) => {
    setAudioTranscriptionExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }, []);

  const adjustComposerTextareaHeight = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    const lineH = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const pad =
      (parseFloat(getComputedStyle(el).paddingTop) || 0) +
      (parseFloat(getComputedStyle(el).paddingBottom) || 0);
    const maxH = lineH * COMPOSER_MAX_LINES + pad;
    const scrollH = el.scrollHeight;
    const next = Math.min(scrollH, maxH);
    const minH = isMobile ? lineH : 40;
    el.style.height = `${Math.max(minH, next)}px`;
    el.style.overflowY = scrollH > maxH ? "auto" : "hidden";
  }, [isMobile]);

  useEffect(() => {
    const el = isMobile || !useWideComposerToolbar
      ? mobileComposerTextareaRef.current
      : desktopComposerTextareaRef.current;
    adjustComposerTextareaHeight(el);
  }, [input, useWideComposerToolbar, adjustComposerTextareaHeight, isMobile]);

  useLayoutEffect(() => {
    const form = composerFormMeasureRef.current;
    if (!form) return;
    const measure = () => {
      const w = form.getBoundingClientRect().width;
      setUseWideComposerToolbar(w >= COMPOSER_TOOLBAR_WIDE_MIN_PX);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(form);
    return () => ro.disconnect();
  }, [activeChannel]);

  // Limpar mensagens locais quando o sinal de limpeza mudar (otimista)
  const clearSignalRef = useRef(clearMessagesSignal);
  useEffect(() => {
    if (clearMessagesSignal > 0 && clearMessagesSignal !== clearSignalRef.current) {
      clearSignalRef.current = clearMessagesSignal;
      for (const conv of conversations) {
        invalidateChatMessageCacheForConversation(conv.conversation_id);
      }
      setMessagesByChannel({});
      setActivityLogs([]);
    }
  }, [clearMessagesSignal, conversations]);

  const insertComposerRef = useRef(insertComposerText?.nonce ?? 0);
  useEffect(() => {
    if (!insertComposerText?.text || insertComposerText.nonce === insertComposerRef.current) return;
    insertComposerRef.current = insertComposerText.nonce;
    setInput(insertComposerText.text);
    setEnhancedText(null);
  }, [insertComposerText]);

  const activeConversation = conversations.find(c => c.channel === activeChannel);

  // Buscar foto de perfil da conta WhatsApp/Instagram
  useEffect(() => {
    if (!activeConversation || !workspaceId) return;

    const fetchAccountProfilePicture = async () => {
      try {
        const { data: conversationData, error } = await supabase
          .from("conversations")
          .select(`
            integration_id,
            customer_service_window_expires_at,
            integrations (
              provider,
              instance_identifier,
              config
            )
          `)
          .eq("id", activeConversation.conversation_id)
          .single();

        if (error) {
 console.error("Error fetching conversation:", error);
          setAccountProfilePicture(null);
          setCloudWindowExpiresAt(null);
          return;
        }

        setCloudWindowExpiresAt(conversationData?.customer_service_window_expires_at ?? null);

        if (conversationData?.integrations) {
          const integration = Array.isArray(conversationData.integrations)
            ? conversationData.integrations[0]
            : conversationData.integrations;

          if (integration?.config?.profile_picture) {
            setAccountProfilePicture(integration.config.profile_picture);
 console.log(' Foto da conta carregada:', integration.config.profile_picture);
          } else {
            setAccountProfilePicture(null);
          }
          const label = activeChannel === "whatsapp"
            ? whatsappIntegrationDisplayLabel(integration)
            : activeChannel === "instagram"
              ? (integration?.config?.username ? `@${integration.config.username}` : integration?.config?.name || "Instagram")
              : (integration?.config?.page_name || "Messenger");
          setAccountOriginLabel(label);
        } else {
          setAccountProfilePicture(null);
          setAccountOriginLabel(null);
        }
      } catch (error) {
 console.error("Error fetching account profile picture:", error);
        setAccountProfilePicture(null);
        setAccountOriginLabel(null);
      }
    };

    fetchAccountProfilePicture();
  }, [activeConversation?.conversation_id, workspaceId, activeChannel]);

  // Carregar providers WhatsApp do workspace (Cloud + UAZAPI).
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("integrations")
        .select("id, provider")
        .eq("workspace_id", workspaceId)
        .in("provider", ["whatsapp_cloud", "whatsapp_uazapi"]);
      if (cancelled) return;
      const providers: Record<string, WhatsAppProviderId> = {};
      const cloudIds = new Set<string>();
      for (const row of data || []) {
        const provider = row.provider as string;
        if (provider === "whatsapp_cloud" || provider === "whatsapp_uazapi") {
          providers[row.id] = provider;
          if (provider === "whatsapp_cloud") cloudIds.add(row.id);
        }
      }
      setIntegrationProviders(providers);
      setCloudIntegrationIds(cloudIds);
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  // Obter foto do cliente (lead)
  const getClientProfilePicture = () => {
    if (activeChannel === 'instagram') {
      return leadMetadata?.instagram_profile_pic || null;
    } else {
      // WhatsApp - buscar do metadata do lead
      return leadMetadata?.profile_picture || null;
    }
  };

  const scrollContainerToBottom = (behavior: ScrollBehavior = "smooth") => {
    const container = messagesScrollContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
      return;
    }
    const top = container.scrollHeight - container.clientHeight;
    if (behavior === "auto") {
      container.scrollTop = top;
    } else {
      container.scrollTo({ top, behavior });
    }
  };

  const finishInitialScroll = useCallback(() => {
    pendingInitialScrollRef.current = false;
    stickToBottomRef.current = true;
    setMessagesViewportReady(true);
    initialScrollResizeObserverRef.current?.disconnect();
    initialScrollResizeObserverRef.current = null;
  }, []);

  const armInitialScrollToBottom = useCallback(() => {
    const container = messagesScrollContainerRef.current;
    if (!container) return;

    scrollContainerToBottom("auto");

    initialScrollResizeObserverRef.current?.disconnect();
    const observer = new ResizeObserver(() => {
      if (!pendingInitialScrollRef.current) return;
      scrollContainerToBottom("auto");
    });
    initialScrollResizeObserverRef.current = observer;
    observer.observe(container);

    requestAnimationFrame(() => {
      if (!pendingInitialScrollRef.current) return;
      scrollContainerToBottom("auto");
      finishInitialScroll();
    });
  }, [finishInitialScroll]);

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleAudio = (index: number) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    if (audioPlayers[index]?.playing) {
      audio.pause();
    } else {
      audio.play();
    }

    setAudioPlayers(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        playing: !prev[index]?.playing
      }
    }));
  };

  const updateTime = (index: number) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    setAudioPlayers(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        currentTime: audio.currentTime,
        duration: audio.duration || 0,
        playbackRate: audio.playbackRate || 1
      }
    }));
  };

  const setPlaybackRate = (index: number, rate: number) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    audio.playbackRate = rate;
    setAudioPlayers(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        playbackRate: rate
      }
    }));
  };

  useEffect(() => {
    const container = messagesScrollContainerRef.current;
    if (!container) return;

    const updateStickToBottom = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 80;
    };

    updateStickToBottom();
    container.addEventListener("scroll", updateStickToBottom, { passive: true });
    return () => container.removeEventListener("scroll", updateStickToBottom);
  }, [activeChannel]);

  useLayoutEffect(() => {
    const container = messagesScrollContainerRef.current;

    if (scrollRestoreAfterLoadOlderRef.current) {
      if (!container) return;
      const { prevScrollTop, prevScrollHeight } = scrollRestoreAfterLoadOlderRef.current;
      scrollRestoreAfterLoadOlderRef.current = null;
      container.scrollTop = prevScrollTop + (container.scrollHeight - prevScrollHeight);
      return;
    }

    if (messagesLoading) return;

    if (pendingInitialScrollRef.current) {
      if (!container) {
        finishInitialScroll();
        return;
      }
      armInitialScrollToBottom();
      return;
    }

    if (!container) return;

    if (stickToBottomRef.current) {
      scrollContainerToBottom("auto");
    }
  }, [messagesByChannel, activeChannel, messagesLoading, armInitialScrollToBottom, finishInitialScroll]);

  useEffect(() => {
    return () => {
      initialScrollResizeObserverRef.current?.disconnect();
      initialScrollResizeObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const allMessages = Object.values(messagesByChannel)
      .flat()
      .filter((message) => !isHubPreviewMessage(message));
    knownMessageIdsRef.current = new Set(
      allMessages.map((message) => message.id).filter((id): id is string => Boolean(id)),
    );
    if (allMessages.length === 0) {
      lastMessageAtRef.current = null;
      return;
    }
    lastMessageAtRef.current = allMessages.reduce((latest, message) =>
      message.created_at && message.created_at > latest ? message.created_at : latest,
    allMessages[0].created_at ?? "");
  }, [messagesByChannel]);

  useEffect(() => {
    messagesByChannelRef.current = messagesByChannel;
  }, [messagesByChannel]);

  const MESSAGE_PAGE_SIZE = CHAT_MESSAGE_PAGE_SIZE;

  // Sincronizar aba de canal ao trocar de conversa/lead (o estado inicial só reflete o primeiro mount).
  useEffect(() => {
    const channel = conversations[0]?.channel;
    if (channel) setActiveChannel(channel);
  }, [leadId, conversations[0]?.conversation_id, conversations[0]?.channel]);

  useEffect(() => {
    pendingInitialScrollRef.current = true;
    stickToBottomRef.current = true;
    setMessagesViewportReady(false);
    initialScrollResizeObserverRef.current?.disconnect();
    initialScrollResizeObserverRef.current = null;
  }, [activeChannel]);

  // Troca de conversa: resetar compositor/seleção sem remontar mensagens (sem key no parent).
  const activeConversationId = conversations[0]?.conversation_id;
  useEffect(() => {
    if (!activeConversationId) return;
    filledExtraPageRef.current = false;
    setInput("");
    setQuotedMessage(null);
    setSelectedMessageIds(new Set());
    setSelectionMode(false);
    setEditingMessage(null);
    setEditingText("");
    sendQueueRef.current = new Map();
    sendQueueProcessingRef.current = new Set();
    setActivePendingSendCount(0);
  }, [activeConversationId]);

    // Carregar mensagens de TODAS as conversas do lead (página que cabe na tela)
  useEffect(() => {
    if (!leadId || conversations.length === 0) return;

    pendingInitialScrollRef.current = true;
    stickToBottomRef.current = true;
    setMessagesViewportReady(false);
    initialScrollResizeObserverRef.current?.disconnect();
    initialScrollResizeObserverRef.current = null;

    const loadGeneration = ++loadGenerationRef.current;
    const conversationIds = conversations.map(c => c.conversation_id);

    const prevIds = prevConversationIdsRef.current;
    if (
      prevIds.length > 0 &&
      makeChatMessageCacheKey(prevIds) !== makeChatMessageCacheKey(conversationIds)
    ) {
      markChatMessageCacheStale(prevIds);
    }
    prevConversationIdsRef.current = conversationIds;

    const cached = getChatMessageCache(conversationIds);
    const cacheIsFresh = Boolean(cached && !isChatMessageCacheStale(cached));

    if (cached) {
      setMessagesByChannel(
        sortMessagesByChannel(cached.messagesByChannel as Record<string, Message[]>) as Record<
          string,
          Message[]
        >,
      );
      setActivityLogs(cached.activityLogs as ActivityLog[]);
      setHasMoreMessages(cached.hasMore);
      setOldestMessageAt(cached.oldestMessageAt);
      setMessagesLoading(false);

      const allCachedMessages = Object.values(cached.messagesByChannel)
        .flat()
        .filter((message) => !isHubPreviewMessage(message));
      knownMessageIdsRef.current = new Set(
        allCachedMessages.map((message) => message.id).filter((id): id is string => Boolean(id)),
      );
      lastMessageAtRef.current = computeLastMessageAt(allCachedMessages);
      serverWatermarkRef.current = lastMessageAtRef.current;
    } else {
      knownMessageIdsRef.current = new Set();
      lastMessageAtRef.current = null;
      serverWatermarkRef.current = null;
      setMessagesByChannel({});
      setActivityLogs([]);
      setHasMoreMessages(false);
      setOldestMessageAt(null);
      setMessagesLoading(true);
    }
    setMessagesError(null);

    const convChannelMap = new Map(conversations.map(c => [c.conversation_id, c.channel]));

    const fetchMessages = async (olderThan?: string): Promise<
      { messages: ServerMessageRow[]; hasMore: boolean } | { error: string }
    > => {
      const pageLimit = MESSAGE_PAGE_SIZE + 1;
      const fromPage = await fetchChatMessageRows(supabase, {
        conversationIds,
        olderThan,
        limit: pageLimit,
      });

      let data: ServerMessageRow[] | null = fromPage;
      if (!fromPage) {
        let query = supabase
          .from("messages")
          .select(CHAT_SYNC_MESSAGE_SELECT)
          .in("conversation_id", conversationIds)
          .order("created_at", { ascending: false })
          .limit(pageLimit);

        if (olderThan) {
          query = query.lt("created_at", olderThan);
        }

        const result = await query;
        if (result.error) {
 console.error(" Erro ao carregar mensagens:", result.error);
          return { error: result.error.message || "Erro ao carregar mensagens" };
        }
        data = (result.data ?? []) as ServerMessageRow[];
      }

      const rows = data ?? [];
      const hasMore = rows.length > MESSAGE_PAGE_SIZE;
      const messages = hasMore ? rows.slice(0, MESSAGE_PAGE_SIZE) : rows;
      return { messages: messages.slice().reverse(), hasMore };
    };

    const enrichMessages = async (
      messagesSnapshot: Record<string, Message[]>,
      generation: number,
    ) => {
      const allMessages = Object.values(messagesSnapshot).flat();
      const messageIds = allMessages
        .map((message) => message.id)
        .filter((id): id is string => Boolean(id));
      if (messageIds.length === 0) return;

      const memberIds = collectMemberIdsFromMessages(allMessages);
      const enrichment = await fetchMessageEnrichment(supabase, messageIds, memberIds);
      if (generation !== loadGenerationRef.current) return;

      setMessagesByChannel((prev) => mergeEnrichmentIntoChannelMessages(prev, enrichment));

      const currentCache = getChatMessageCache(conversationIds);
      if (currentCache) {
        setChatMessageCache(conversationIds, {
          ...currentCache,
          messagesByChannel: mergeEnrichmentIntoChannelMessages(
            currentCache.messagesByChannel as Record<string, Message[]>,
            enrichment,
          ),
        });
      }
    };

    // Buscar logs de atividade
    const fetchActivityLogs = async (bounds: ActivityWindow & { append?: boolean } = {}) => {
      if (loadGeneration !== loadGenerationRef.current) return;

      let logsQuery = supabase
        .from("member_activity_log")
        .select(`
            id,
            action_type,
            created_at,
            conversation_id,
            metadata,
            member:workspace_members!member_activity_log_member_id_fkey(display_name)
          `)
        .in("conversation_id", conversationIds)
        .in("action_type", ["ai_enabled", "ai_disabled", "lead_assigned", "lead_unassigned", "conversation_closed", "conversation_reopened"]);
      let eventsQuery = supabase
        .from("conversation_state_events")
        .select("id, conversation_id, previous_state, new_state, reason, actor_type, actor_label, metadata, created_at")
        .in("conversation_id", conversationIds);

      if (bounds.since) {
        logsQuery = logsQuery.gte("created_at", bounds.since);
        eventsQuery = eventsQuery.gte("created_at", bounds.since);
      }
      if (bounds.until) {
        logsQuery = logsQuery.lt("created_at", bounds.until);
        eventsQuery = eventsQuery.lt("created_at", bounds.until);
      }

      const [logsRes, eventsRes] = await Promise.all([
        logsQuery.order("created_at", { ascending: true }).limit(CHAT_ACTIVITY_WINDOW_LIMIT),
        eventsQuery.order("created_at", { ascending: true }).limit(CHAT_ACTIVITY_WINDOW_LIMIT),
      ]);

      if (logsRes.error) {
 console.error(" Erro ao carregar logs de atividade:", logsRes.error);
      }
      if (eventsRes.error) {
 console.error(" Erro ao carregar eventos de estado:", eventsRes.error);
      }

      const memberLogs = (logsRes.data || []) as any[];

      // Map state events into the same ActivityLog shape so the existing renderer picks them up.
      const stateLogs: any[] = (eventsRes.data || [])
        .filter((e: any) => e.new_state === 'paused' || e.new_state === 'active')
        .map((e: any) => {
          const isBackfill = e.reason === 'backfill_initial';
          const action_type = isBackfill
            ? (e.new_state === 'active' ? 'state_initial_active' : 'state_initial_paused')
            : (e.new_state === 'active' ? 'ai_enabled' : 'ai_disabled');
          // Friendly attribution
          let displayName: string | null = e.actor_label || null;
          let reasonLabel: string | null = null;

          if (e.reason === "agent_routed") {
            const meta = (e.metadata ?? {}) as Record<string, unknown>;
            reasonLabel = formatAgentTransferLabel(meta);
            displayName = null;
          } else if (
            e.reason === "lead_stage_auto_disable_agent" ||
            e.reason === "lead_stage_atendimento_humano"
          ) {
            const meta = (e.metadata ?? {}) as Record<string, unknown>;
            const stageName =
              (typeof meta.stage_name === "string" && meta.stage_name) ||
              (typeof meta.new_stage === "string" && meta.new_stage) ||
              null;
            reasonLabel = stageName
              ? `Lead movido para ${stageName}`
              : "Lead movido para estágio com IA desligada";
            // Mantém displayName se o trigger conseguiu identificar quem moveu
          } else if (!displayName) {
            if (e.actor_type === 'external_device') displayName = 'Celular físico';
            else if (e.actor_type === 'system') {
              if (e.reason === 'no_active_agents') displayName = 'Sistema (nenhum agente ativo)';
              else if (e.reason === 'human_takeover') displayName = 'Celular físico';
              else if (e.reason === 'agent_deactivated') displayName = 'Sistema (agente IA desativado)';
              else if (e.reason === 'agent_reactivated') displayName = 'Sistema (agente IA reativado)';
              else if (e.reason === 'agent_disabled' || e.reason === 'agent_enabled') displayName = 'Sistema (toggle de agente)';
              else if (e.reason === 'ai_handoff_tool') displayName = 'IA (passou para humano)';
              else if (e.reason === 'complaint_detected') displayName = 'IA (reclamação detectada)';
              else if (e.reason === 'handoff_enforcer_retry') displayName = 'IA (handoff garantido)';
              else if (e.reason === 'member_manual_reply') displayName = 'Membro (resposta manual)';
              else if (e.reason === 'manual_toggle') displayName = 'Membro';
              else if (e.reason === 'subscription_paused') displayName = 'Sistema (assinatura)';
              else if (e.reason === 'incident_recovery') displayName = null;
              else if (isBackfill) displayName = null;
              else displayName = 'Sistema';
            }
          }

          return {
            id: `state:${e.id}`,
            action_type,
            created_at: e.created_at,
            conversation_id: e.conversation_id,
            metadata: { member_name: displayName, reason: e.reason, reason_label: reasonLabel, actor_type: e.actor_type },
            member: null,
          };
        });

      // Dedup: if a member_activity_log already exists within ±2s of a state event with same action_type,
      // prefer the member_activity_log (richer member data).
      const merged = [...memberLogs];
      for (const sl of stateLogs) {
        const slTime = new Date(sl.created_at).getTime();
        const dup = memberLogs.find((ml: any) =>
          ml.action_type === sl.action_type &&
          ml.conversation_id === sl.conversation_id &&
          Math.abs(new Date(ml.created_at).getTime() - slTime) < 2000
        );
        if (!dup) merged.push(sl);
      }
      merged.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      if (loadGeneration !== loadGenerationRef.current) return;
      const currentCache = getChatMessageCache(conversationIds);
      const nextLogs = bounds.append
        ? mergeActivityLogs(currentCache?.activityLogs ?? [], merged)
        : merged;
      setActivityLogs(nextLogs);

      if (currentCache) {
        setChatMessageCache(conversationIds, {
          ...currentCache,
          activityLogs: nextLogs,
        });
      }
    };

    const applySuccessfulFetch = async (
      result: { messages: ServerMessageRow[]; hasMore: boolean },
      options: { runReconcile: boolean },
    ) => {
      noteServerRows(result.messages);
      setHasMoreMessages(result.hasMore);
      const oldestAt = result.messages.length > 0 ? result.messages[0].created_at : null;
      if (oldestAt) {
        setOldestMessageAt(oldestAt);
      }

      const grouped = groupRowsByChannel(result.messages, convChannelMap);
      let mergedSnapshot: Record<string, Message[]> = {};

      setMessagesByChannel((prev) => {
        const merged = mergeInitialMessagesLoad(prev, grouped as Record<string, Message[]>);
        const allMessages = Object.values(merged).flat().filter((message) => !isHubPreviewMessage(message));
        knownMessageIdsRef.current = new Set(
          allMessages.map((message) => message.id).filter((id): id is string => Boolean(id)),
        );
        lastMessageAtRef.current = computeLastMessageAt(allMessages);
        mergedSnapshot = merged;
        return merged;
      });

      setMessagesLoading(false);

      if (options.runReconcile) {
        await reconcileChatRef.current?.();
        if (loadGeneration !== loadGenerationRef.current) return;
      }

      void enrichMessages(mergedSnapshot, loadGeneration);

      const activityLogsPromise = fetchActivityLogs({ since: oldestAt });
      setChatMessageCache(conversationIds, {
        messagesByChannel: sortMessagesByChannel(mergedSnapshot),
        activityLogs: getChatMessageCache(conversationIds)?.activityLogs ?? cached?.activityLogs ?? [],
        oldestMessageAt: oldestAt,
        hasMore: result.hasMore,
      });
      await activityLogsPromise;
    };

    const refreshMessagesFromServer = async (options: { runReconcile: boolean }) => {
      const result = await dedupeMessageFetch(conversationIds, () => fetchMessages());
      if (loadGeneration !== loadGenerationRef.current) return;

      if ("error" in result) {
        if (!cached) {
          setMessagesError(result.error);
          setMessagesLoading(false);
        }
        return;
      }

      await applySuccessfulFetch(result, options);
    };

    fetchActivityWindowRef.current = fetchActivityLogs;

    const loadInitialMessages = async () => {
      if (cacheIsFresh) {
        return;
      }

      if (cached && isChatMessageCacheStale(cached)) {
        cancelBackgroundRefresh(conversationIds);
        void refreshMessagesFromServer({ runReconcile: false });
        return;
      }

      await refreshMessagesFromServer({ runReconcile: false });
    };

    const mapIncomingMessage = (row: ServerMessageRow): Message =>
      serverRowToChatMessage(row) as Message;

    const applyMessagesToState = (rows: any[]) => {
      if (rows.length === 0) return;
      setMessagesByChannel((prev) => {
        let next = prev;
        let changed = false;
        const confirmedByChannel: Record<string, Message[]> = {};

        for (const row of rows) {
          const msgChannel = convChannelMap.get(row.conversation_id);
          if (!msgChannel) continue;
          if (isLegacyInstagramReactionMessage(row.metadata)) continue;

          const incoming = mapIncomingMessage(row);
          if (!incoming.id) continue;

          const channelMsgs = next[msgChannel] || [];
          const index = channelMsgs.findIndex((message) => message.id === incoming.id);
          if (index === -1) {
            next = mergeMessagesByChannel(next, { [msgChannel]: [incoming] });
            changed = true;
            if (!confirmedByChannel[msgChannel]) confirmedByChannel[msgChannel] = [];
            confirmedByChannel[msgChannel].push(incoming);
            continue;
          }

          const patched = patchMessageFromServerRow(channelMsgs[index], row as ServerMessageRow);
          if (patched === channelMsgs[index]) continue;
          const updatedMsgs = [...channelMsgs];
          updatedMsgs[index] = patched;
          next = { ...next, [msgChannel]: updatedMsgs };
          changed = true;
          if (!confirmedByChannel[msgChannel]) confirmedByChannel[msgChannel] = [];
          confirmedByChannel[msgChannel].push(patched);
        }

        for (const [channel, confirmed] of Object.entries(confirmedByChannel)) {
          const channelMsgs = next[channel];
          if (!channelMsgs?.length) continue;
          const filtered = removeSupersededHubPreviews(channelMsgs, confirmed);
          if (filtered.length !== channelMsgs.length) {
            next = { ...next, [channel]: filtered };
            changed = true;
          }
        }

        return changed ? next : prev;
      });
    };

    applyServerMessagesRef.current = (rows) => applyMessagesToState(rows);

    syncNewMessagesRef.current = async () => {
      const generation = loadGenerationRef.current;
      // Marca d'água = só linhas vindas do servidor (não o envio local), com volta
      // de alguns minutos: entradas usam o horário do WhatsApp e podem chegar atrasadas.
      const since = sinceWithOverlap(serverWatermarkRef.current, CHAT_GAP_OVERLAP_MS);
      const fromPage = await fetchChatMessageRows(supabase, {
        conversationIds,
        since,
        limit: since ? CHAT_MESSAGE_GAP_LIMIT : MESSAGE_PAGE_SIZE,
      });

      let data: ServerMessageRow[] | null = fromPage;
      const usedRpc = fromPage != null;
      if (!fromPage) {
        let query = supabase
          .from("messages")
          .select(CHAT_SYNC_MESSAGE_SELECT)
          .in("conversation_id", conversationIds)
          .order("created_at", { ascending: true });

        if (since) {
          query = query.gt("created_at", since).limit(CHAT_MESSAGE_GAP_LIMIT);
        } else {
          query = query.limit(MESSAGE_PAGE_SIZE);
        }

        const result = await query;
        if (result.error) {
 console.error(" Erro ao sincronizar mensagens:", result.error);
          return;
        }
        data = (result.data ?? []) as ServerMessageRow[];
      }
      if (generation !== loadGenerationRef.current) return;
      if (usedRpc && !since) data = (data ?? []).slice().reverse();
      const rows = data ?? [];
      noteServerRows(rows);
      const known = knownMessageIdsRef.current;
      const hasNew = rows.some((row) => row.id && !known.has(row.id));
      if (hasNew) {
        for (const id of conversationIds) {
          invalidateChatMessageCacheForConversation(id);
        }
      }
      applyMessagesToState(rows);
    };

    let openChatSyncTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingConversationLastAt: string | null = null;

    requestSyncNewMessagesRef.current = (conversationLastMessageAt: string) => {
      if (!pendingConversationLastAt || conversationLastMessageAt > pendingConversationLastAt) {
        pendingConversationLastAt = conversationLastMessageAt;
      }
      if (openChatSyncTimer) return;

      openChatSyncTimer = setTimeout(() => {
        openChatSyncTimer = null;
        const generation = loadGenerationRef.current;
        const target = pendingConversationLastAt;
        pendingConversationLastAt = null;
        if (generation !== loadGenerationRef.current || !target) return;
        // O aviso realtime da mensagem pode ter chegado durante a espera.
        const knownCreatedAts = Object.values(messagesByChannelRef.current)
          .flat()
          .filter((message) => !isHubPreviewMessage(message))
          .map((message) => message.created_at);
        if (hasMessageAtInstant(knownCreatedAts, target)) return;
        void syncNewMessagesRef.current?.();
      }, INBOX_OPEN_CHAT_SYNC_DELAY_MS);
    };

    applyHubOutboundPreviewRef.current = (conversationId, preview, lastMessageAt) => {
      const channel = convChannelMap.get(conversationId);
      if (!channel || !preview.trim()) return;

      setMessagesByChannel((prev) => {
        const channelMsgs = prev[channel] || [];
        const previewId = buildHubPreviewMessageId(conversationId, lastMessageAt);

        const hasConfirmed = channelMsgs.some(
          (message) =>
            message.role === "user" &&
            message.id &&
            !isHubPreviewMessage(message) &&
            (message.created_at === lastMessageAt ||
              hubPreviewMatchesOutboundBody(preview, message.content ?? "")),
        );
        if (hasConfirmed || channelMsgs.some((message) => message.id === previewId)) {
          return prev;
        }

        const previewMessage: Message = {
          id: previewId,
          role: "user",
          content: preview,
          created_at: lastMessageAt,
          delivery_status: "pending",
          metadata: { source: HUB_PREVIEW_MESSAGE_SOURCE },
        };

        return mergeMessagesByChannel(prev, { [channel]: [previewMessage] });
      });
    };

    reconcilePendingMessagesRef.current = async () => {
      const generation = loadGenerationRef.current;
      const pendingIds = collectStaleOutboundMessageIds(messagesByChannelRef.current);
      if (pendingIds.length === 0) return;

      const { data, error } = await supabase
        .from("messages")
        .select(CHAT_SYNC_MESSAGE_SELECT)
        .in("id", pendingIds);

      if (error) {
 console.error(" Erro ao reconciliar mensagens pendentes:", error);
        return;
      }
      if (generation !== loadGenerationRef.current) return;
      applyMessagesToState(data ?? []);
    };

    reconcileChatRef.current = async () => {
      await syncNewMessagesRef.current?.();
      await reconcilePendingMessagesRef.current?.();
    };

    void loadInitialMessages();

    // Realtime por escopo: uma conversa aberta = um canal (chat-${leadId}).
    // Troca de conversa = desmonta este canal e abre outro; nunca escutar messages globalmente.
    const conversationFilter =
      conversationIds.length === 1
        ? `conversation_id=eq.${conversationIds[0]}`
        : `conversation_id=in.(${conversationIds.join(",")})`;
    const channelName = `chat-${leadId}`;
    const realtimeChannel = subscribePostgresChannelResilient(
      channelName,

      [
        // INSERT + UPDATE no mesmo binding: metade das subscriptions no servidor
        // de realtime (menos checagens de RLS por evento).
        {
          binding: {
            event: "*",
            schema: "public",
            table: "messages",
            filter: conversationFilter,
          },
          callback: (payload: any) => {
            const row = payload.new as { conversation_id?: string; id?: string } | null;
            if (!row?.conversation_id || !conversationIds.includes(row.conversation_id)) return;

            if (payload.eventType === "INSERT") {
              noteServerRows([payload.new]);
              invalidateChatMessageCacheForConversation(row.conversation_id);
              applyMessagesToState([payload.new]);
              return;
            }

            if (payload.eventType !== "UPDATE") return;

            const updated = payload.new;
            noteServerRows([updated]);
            const msgChannel = convChannelMap.get(updated.conversation_id);
            if (!msgChannel) return;

            setMessagesByChannel((prev) => {
              const channelMsgs = prev[msgChannel] || [];
              const idx = channelMsgs.findIndex((m) => m.id === updated.id);
              if (idx === -1) {
                const grouped: { [channel: string]: Message[] } = {
                  [msgChannel]: [mapIncomingMessage(updated)],
                };
                return mergeMessagesByChannel(prev, grouped);
              }

              const updatedMsgs = [...channelMsgs];
              updatedMsgs[idx] = patchMessageFromServerRow(updatedMsgs[idx], updated);
              return { ...prev, [msgChannel]: updatedMsgs };
            });
          },
        },
        {
          binding: {
            event: "INSERT",
            schema: "public",
            table: "member_activity_log",
            filter: conversationFilter,
          },
          callback: (payload: any) => {
            const convId = payload.new?.conversation_id;
            if (!convId || !conversationIds.includes(convId)) return;
            const allowedTypes = ["ai_enabled", "ai_disabled", "lead_assigned", "lead_unassigned", "conversation_closed", "conversation_reopened"];
            if (!allowedTypes.includes(payload.new?.action_type)) return;
            const newLog: ActivityLog = {
              id: payload.new.id,
              action_type: payload.new.action_type ?? "",
              created_at: payload.new.created_at ?? new Date().toISOString(),
              metadata: payload.new.metadata,
              member: null,
            };
            setActivityLogs(prev => [...prev, newLog]);
            if (activityLogsDebounceRef.current) clearTimeout(activityLogsDebounceRef.current);
            activityLogsDebounceRef.current = setTimeout(() => {
              activityLogsDebounceRef.current = null;
              fetchActivityLogs();
            }, 2000);
          },
        },
        // Sem filter por conversation_id: cada evento passa por RLS em message_reactions
        // (custo maior que bindings filtrados). knownMessageIdsRef limita updates ao chat aberto.
        {
          binding: {
            event: "*",
            schema: "public",
            table: "message_reactions",
          },
          callback: (payload: any) => {
            const reaction = payload.new;
            const oldReaction = payload.old;
            const messageId = reaction?.message_id ?? oldReaction?.message_id;
            if (!messageId || !knownMessageIdsRef.current.has(messageId)) return;

            if (payload.eventType === "DELETE" && oldReaction?.message_id) {
              setMessagesByChannel(prev => {
                const updated = { ...prev };
                for (const ch of Object.keys(updated)) {
                  updated[ch] = updated[ch].map(m => {
                    if (m.id !== oldReaction.message_id) return m;
                    return {
                      ...m,
                      reactions: (m.reactions || []).filter(r => r.from_user !== oldReaction.from_user)
                    };
                  });
                }
                return updated;
              });
              return;
            }

            if (reaction?.message_id) {
              setMessagesByChannel(prev => {
                const updated = { ...prev };
                for (const ch of Object.keys(updated)) {
                  updated[ch] = updated[ch].map(m => {
                    if (m.id !== reaction.message_id) return m;
                    const existingReactions = (m.reactions || []).filter(r => r.from_user !== reaction.from_user);
                    return {
                      ...m,
                      reactions: [...existingReactions, { reaction: reaction.reaction, from_user: reaction.from_user }]
                    };
                  });
                }
                return updated;
              });
            }
          },
        },
      ],
      {
        onSubscribed: (isReconnect) => {
          // Após (re)assinar, preencher o gap de mensagens que chegaram enquanto o canal estava fora.
          if (isReconnect) void reconcileChatRef.current?.();
        },
      },
    );

    realtimeHandleRef.current = realtimeChannel;
    realtimeChannelsCountDelta(1);

    return () => {
      if (openChatSyncTimer) clearTimeout(openChatSyncTimer);
      cancelBackgroundRefresh(conversationIds);
      markChatMessageCacheStale(conversationIds);
      syncNewMessagesRef.current = null;
      fetchActivityWindowRef.current = null;
      requestSyncNewMessagesRef.current = null;
      applyHubOutboundPreviewRef.current = null;
      reconcilePendingMessagesRef.current = null;
      reconcileChatRef.current = null;
      applyServerMessagesRef.current = null;
      realtimeChannelsCountDelta(-1);
      realtimeChannel.unsubscribe();
      realtimeHandleRef.current = null;
      if (activityLogsDebounceRef.current) {
        clearTimeout(activityLogsDebounceRef.current);
        activityLogsDebounceRef.current = null;
      }
    };

  }, [leadId, JSON.stringify(conversations.map(c => c.conversation_id))]);

  const openConversationIdsKey = JSON.stringify(conversations.map((c) => c.conversation_id));
  useEffect(() => {
    openConversationIdsRef.current = conversations.map((c) => c.conversation_id);
  }, [openConversationIdsKey, conversations]);

  const handleOpenConversationReadModelUpdate = useCallback<ConversationChangeHandler>(
    (payload) => {
      if (payload.eventType !== "UPDATE" || !payload.new) return;

      const row = payload.new as Record<string, unknown>;
      const convId = typeof row.id === "string" ? row.id : null;
      if (!convId) return;

      if (!openConversationIdsRef.current.includes(convId)) return;

      const lastAt = typeof row.last_message_at === "string" ? row.last_message_at : null;
      if (!lastAt) return;

      // A conversa aponta para uma mensagem que já está na tela: nada a buscar.
      // Comparar só "mais nova que a última" perdia entradas com horário do WhatsApp
      // mais antigo que uma saída já exibida (caso Sindilojas 13/10).
      const knownCreatedAts = Object.values(messagesByChannelRef.current)
        .flat()
        .filter((message) => !isHubPreviewMessage(message))
        .map((message) => message.created_at);
      if (hasMessageAtInstant(knownCreatedAts, lastAt)) return;

      const since = lastMessageAtRef.current;
      if (row.last_message_direction === "outbound" && !(since && lastAt < since)) {
        const preview =
          typeof row.last_message_preview === "string" ? row.last_message_preview.trim() : "";
        if (preview) {
          applyHubOutboundPreviewRef.current?.(convId, preview, lastAt);
        }
      }

      requestSyncNewMessagesRef.current?.(lastAt);
    },
    [],
  );

  const handleHubReconcile = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    const handle = realtimeHandleRef.current;
    if (!handle || !shouldRefetchChatOnFocus(handle.isHealthy())) return;
    handle.ensureHealthy();
  }, []);

  useWorkspaceConversationRealtime(
    workspaceId,
    handleOpenConversationReadModelUpdate,
    Boolean(workspaceId && leadId),
    REALTIME_PAUSE_ON_HIDDEN_ENABLED ? handleHubReconcile : undefined,
  );

  useEffect(() => {
    if (!leadId) return;

    const reconcileChat = () => {
      if (document.visibilityState !== "visible") return;
      const handle = realtimeHandleRef.current;
      if (!handle || !shouldRefetchChatOnFocus(handle.isHealthy())) return;
      handle.ensureHealthy();
    };

    document.addEventListener("visibilitychange", reconcileChat);
    window.addEventListener("focus", reconcileChat);
    window.addEventListener("pageshow", reconcileChat);
    window.addEventListener("online", reconcileChat);

    // Watchdog único: só inspeciona estado em memória; não faz polling de mensagens.
    const watchdogId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;

      const handle = realtimeHandleRef.current;
      if (handle && !handle.isHealthy()) {
        handle.ensureHealthy();
        return;
      }

      if (!hasStaleOutboundMessages(messagesByChannelRef.current)) return;
      void reconcilePendingMessagesRef.current?.();
    }, 30000);

    return () => {
      document.removeEventListener("visibilitychange", reconcileChat);
      window.removeEventListener("focus", reconcileChat);
      window.removeEventListener("pageshow", reconcileChat);
      window.removeEventListener("online", reconcileChat);
      window.clearInterval(watchdogId);
    };

  }, [leadId]);

  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const updateKeyboardOffset = () => {
      const offset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      document.documentElement.style.setProperty("--chat-keyboard-offset", `${offset}px`);
    };

    updateKeyboardOffset();
    viewport.addEventListener("resize", updateKeyboardOffset);
    viewport.addEventListener("scroll", updateKeyboardOffset);

    return () => {
      viewport.removeEventListener("resize", updateKeyboardOffset);
      viewport.removeEventListener("scroll", updateKeyboardOffset);
      document.documentElement.style.removeProperty("--chat-keyboard-offset");
    };
  }, [isMobile]);

  // Load older messages (reverse pagination)
  const loadOlderMessages = async () => {
    if (!oldestMessageAt || loadingMore || !hasMoreMessages) return;
    setLoadingMore(true);

    const conversationIds = conversations.map(c => c.conversation_id);
    const convChannelMap = new Map(conversations.map(c => [c.conversation_id, c.channel]));
    const generation = loadGenerationRef.current;
    const previousOldest = oldestMessageAt;
    const pageLimit = MESSAGE_PAGE_SIZE + 1;

    const fromPage = await fetchChatMessageRows(supabase, {
      conversationIds,
      olderThan: oldestMessageAt,
      limit: pageLimit,
    });

    let data: ServerMessageRow[] | null = fromPage;
    if (!fromPage) {
      const result = await supabase
        .from("messages")
        .select(CHAT_SYNC_MESSAGE_SELECT)
        .in("conversation_id", conversationIds)
        .lt("created_at", oldestMessageAt)
        .order("created_at", { ascending: false })
        .limit(pageLimit);

      if (result.error) {
        setLoadingMore(false);
        return;
      }
      data = (result.data ?? []) as ServerMessageRow[];
    }

    const rows = data ?? [];
    const hasMore = rows.length > MESSAGE_PAGE_SIZE;
    const messages = hasMore ? rows.slice(0, MESSAGE_PAGE_SIZE) : rows;
    const reversed = messages.slice().reverse();

    setHasMoreMessages(hasMore);
    if (reversed.length > 0) {
      setOldestMessageAt(reversed[0].created_at);
      void fetchActivityWindowRef.current?.({
        since: reversed[0].created_at,
        until: previousOldest,
        append: true,
      });
    }

    const newGrouped = groupRowsByChannel(reversed, convChannelMap) as Record<string, Message[]>;

    if (reversed.length > 0 && messagesScrollContainerRef.current) {
      const el = messagesScrollContainerRef.current;
      scrollRestoreAfterLoadOlderRef.current = { prevScrollTop: el.scrollTop, prevScrollHeight: el.scrollHeight };
    }

    setMessagesByChannel(prev => {
      const updated = { ...prev };
      for (const ch of Object.keys(newGrouped)) {
        updated[ch] = sortMessagesChronologically([...newGrouped[ch], ...(prev[ch] || [])]);
      }
      return updated;
    });

    for (const id of conversationIds) {
      invalidateChatMessageCacheForConversation(id);
    }

    setLoadingMore(false);

    if (generation !== loadGenerationRef.current) return;

    const allNewMessages = Object.values(newGrouped).flat();
    const messageIds = allNewMessages
      .map((message) => message.id)
      .filter((id): id is string => Boolean(id));
    if (messageIds.length > 0) {
      const memberIds = collectMemberIdsFromMessages(allNewMessages);
      const enrichment = await fetchMessageEnrichment(supabase, messageIds, memberIds);
      if (generation !== loadGenerationRef.current) return;
      setMessagesByChannel((prev) => mergeEnrichmentIntoChannelMessages(prev, enrichment));
    }
  };

  useEffect(() => {
    if (!messagesViewportReady || messagesLoading || loadingMore || !hasMoreMessages) return;
    if (filledExtraPageRef.current) return;
    const container = messagesScrollContainerRef.current;
    if (!container || container.clientHeight <= 0) return;
    if (container.scrollHeight > container.clientHeight + 8) return;
    filledExtraPageRef.current = true;
    void loadOlderMessages();
  }, [messagesViewportReady, messagesLoading, loadingMore, hasMoreMessages, activeConversationId]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setInput((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    setMobileEmojiDialogOpen(false);
  };

  const [isSendingReaction, setIsSendingReaction] = useState(false);

  const handleSendReaction = async (messageId: string, emoji: string) => {
    if (isSendingReaction) return;
    try {
      setIsSendingReaction(true);
      const conv = conversations.find(c => c.channel === activeChannel);
      if (!conv || (conv.channel !== "whatsapp" && conv.channel !== "instagram")) {
        toast({
          title: "❌ Não disponível",
          description: "Reações só estão disponíveis para WhatsApp e Instagram",
          variant: "destructive"
        });
        return;
      }

      // Optimistic update
      setMessagesByChannel(prev => {
        const channelMsgs = prev[activeChannel] || [];
        return {
          ...prev,
          [activeChannel]: channelMsgs.map(m => {
            if (m.id !== messageId) return m;
            const existingReactions = (m.reactions || []).filter(r => r.from_user !== "assistant");
            return { ...m, reactions: [...existingReactions, { reaction: emoji, from_user: "assistant" }] };
          })
        };
      });

      setReactionPickerIndex(null);
      setHoveredMessageIndex(null);

      const { error } = await supabase.functions.invoke("send-reaction", {
        body: {
          messageId,
          conversationId: conv.conversation_id,
          reaction: emoji
        }
      });

      if (error) throw error;
    } catch (error: any) {
 console.error("Erro ao enviar reação:", error);
      toast({
        title: "❌ Erro ao enviar reação",
        description: error.message || "Não foi possível enviar a reação",
        variant: "destructive"
      });
    } finally {
      setIsSendingReaction(false);
    }
  };

  const handleDeleteForEveryone = async (messageId: string) => {
    try {
      const { error } = await supabase.functions.invoke("delete-message-for-everyone", {
        body: { messageId },
      });
      if (error) throw error;

      setMessagesByChannel(prev => {
        const updated = { ...prev };
        for (const channel of Object.keys(updated)) {
          updated[channel] = updated[channel].filter(m => m.id !== messageId);
        }
        return updated;
      });
      toast({ title: "Mensagem apagada para todos" });
    } catch (error: any) {
 console.error("Erro ao apagar para todos:", error);
      toast({
        title: "Erro ao apagar para todos",
        description: error?.message || "Tente novamente",
        variant: "destructive",
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editingMessage) return;
    const newText = editingText.trim();
    if (!newText) {
      toast({ title: "A mensagem não pode ficar vazia", variant: "destructive" });
      return;
    }
    if (newText === editingMessage.body) {
      setEditingMessage(null);
      return;
    }
    setIsSavingEdit(true);
    try {
      const { error } = await supabase.functions.invoke("edit-message", {
        body: { messageId: editingMessage.id, newText },
      });
      if (error) throw error;

      setMessagesByChannel(prev => {
        const updated = { ...prev };
        for (const channel of Object.keys(updated)) {
          updated[channel] = updated[channel].map(m =>
            m.id === editingMessage.id
              ? {
                  ...m,
                  content: newText,
                  body: newText,
                  metadata: {
                    ...(m.metadata as any),
                    edited_at: new Date().toISOString(),
                  },
                }
              : m
          );
        }
        return updated;
      });
      setEditingMessage(null);
      toast({ title: "Mensagem editada" });
    } catch (error: any) {
 console.error("Erro ao editar mensagem:", error);
      toast({
        title: "Erro ao editar mensagem",
        description: error?.message || "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setIsSavingEdit(false);
    }
  };


  const handleDeleteMessages = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      const { error } = await supabase
        .from("messages")
        .delete()
        .in("id", ids);
      if (error) throw error;

      // Remove from local state
      setMessagesByChannel(prev => {
        const updated = { ...prev };
        for (const channel of Object.keys(updated)) {
          updated[channel] = updated[channel].filter(m => !ids.includes(m.id || ""));
        }
        return updated;
      });

      setSelectedMessageIds(new Set());
      setSelectionMode(false);
      toast({ title: `${ids.length} mensagem${ids.length > 1 ? "ns" : ""} excluída${ids.length > 1 ? "s" : ""}` });
    } catch (error: any) {
 console.error("Erro ao excluir mensagens:", error);
      toast({
        title: "Erro ao excluir mensagens",
        description: error.message || "Tente novamente",
        variant: "destructive"
      });
    }
  };

  const toggleMessageSelection = (msgId: string) => {
    setSelectedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const getMediaType = (file: File): 'image' | 'audio' | 'video' | 'document' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.startsWith('video/')) return 'video';
    return 'document';
  };

  const canAcceptImageAttachment = useCallback(
    (conv: ChatInterfaceProps["conversations"][number]) => {
      if (isLoading || selectionMode) return false;
      if (conv.state === "active" || conv.state === "closed") return false;
      if (conv.channel === "instagram" && conv.state !== "paused") return false;
      return true;
    },
    [isLoading, selectionMode]
  );

  const processAttachmentFile = useCallback((file: File) => {
    const allowedTypes =
      activeChannel === "instagram"
        ? [
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/gif",
            "application/pdf",
            "application/x-pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/plain",
            "audio/mpeg",
            "audio/mp3",
            "audio/mp4",
            "audio/ogg",
            "audio/webm",
            "audio/webm;codecs=opus",
            "audio/wav",
            "audio/aac",
          ]
        : [
            // Imagens
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/gif",
            // Vídeos
            "video/mp4",
            "video/webm",
            // Áudios
            "audio/mp3",
            "audio/mpeg",
            "audio/ogg",
            "audio/mp4",
            "audio/webm",
            "audio/wav",
            "audio/aac",
            // Documentos
            "application/pdf",
            "application/x-pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/plain",
          ];

    if (!allowedTypes.includes(file.type)) {
      // Fallback: checar extensão do arquivo quando MIME type não é reconhecido
      const ext = file.name.split(".").pop()?.toLowerCase();
      const validExtensions =
        activeChannel === "instagram"
          ? [
              "jpg",
              "jpeg",
              "png",
              "webp",
              "gif",
              "pdf",
              "doc",
              "docx",
              "xls",
              "xlsx",
              "txt",
              "mp3",
              "m4a",
              "mp4",
              "ogg",
              "webm",
              "wav",
              "aac",
            ]
          : [
              "jpg",
              "jpeg",
              "png",
              "webp",
              "gif",
              "mp3",
              "ogg",
              "mp4",
              "webm",
              "wav",
              "aac",
              "pdf",
              "doc",
              "docx",
              "xls",
              "xlsx",
              "txt",
            ];
      if (!ext || !validExtensions.includes(ext)) {
        toast({
          title: "Tipo de arquivo não suportado",
          description:
            activeChannel === "instagram"
              ? "No Instagram envie imagens, áudios (MP3, M4A, OGG, WebM…) ou documentos (PDF, Word, Excel, TXT)."
              : "Envie imagens (JPG, PNG, WEBP), áudios (MP3, OGG) ou documentos (PDF, Word, Excel, TXT)",
          variant: "destructive",
        });
        return;
      }
    }

    if (activeChannel === "instagram") {
      if (file.type.startsWith("image/") && file.size > 8 * 1024 * 1024) {
        toast({
          title: "Imagem muito grande",
          description: "No Instagram o limite indicado pela Meta para imagens é cerca de 8MB.",
          variant: "destructive",
        });
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: "Arquivo muito grande",
          description: "Limite de ~25MB para áudio ou documento no Instagram (política da Meta).",
          variant: "destructive",
        });
        return;
      }
    } else if (file.size > 16 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "Tamanho máximo: 16MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    }

    // Resetar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [activeChannel, toast]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processAttachmentFile(file);
  };

  const attachDroppedOrPastedImages = useCallback(
    (files: File[], conv: ChatInterfaceProps["conversations"][number]) => {
      if (!canAcceptImageAttachment(conv)) {
        if (!activeConversation) {
          toast({
            title: "Conversa indisponível",
            description: "Selecione uma conversa para anexar imagens.",
            variant: "destructive",
          });
        } else if (conv.state === "active") {
          toast({
            title: "⚠️ Agente ativo",
            description: "Pause o agente para anexar imagens.",
            variant: "destructive",
          });
        } else if (conv.state === "closed") {
          toast({
            title: "⚠️ Conversa encerrada",
            description: "Esta conversa foi encerrada.",
            variant: "destructive",
          });
        }
        return;
      }

      const images = files.filter(isImageAttachmentFile);
      if (images.length === 0) {
        toast({
          title: "Somente imagens",
          description: "Arraste ou cole apenas arquivos de imagem (JPG, PNG, WEBP, GIF).",
          variant: "destructive",
        });
        return;
      }

      if (images.length > 1) {
        toast({
          title: "Uma imagem por vez",
          description: "Usando a primeira imagem selecionada.",
        });
      }

      processAttachmentFile(images[0]);
    },
    [activeConversation, canAcceptImageAttachment, processAttachmentFile, toast]
  );

  const resetChatDragState = useCallback(() => {
    chatDragDepthRef.current = 0;
    setIsChatDragOver(false);
  }, []);

  const createChatDragHandlers = useCallback(
    (conv: ChatInterfaceProps["conversations"][number]) => ({
      onDragEnter: (e: React.DragEvent) => {
        if (!dataTransferHasImages(e.dataTransfer)) return;
        e.preventDefault();
        chatDragDepthRef.current += 1;
        setIsChatDragOver(true);
      },
      onDragOver: (e: React.DragEvent) => {
        if (!dataTransferHasImages(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = canAcceptImageAttachment(conv) ? "copy" : "none";
        setIsChatDragOver(true);
      },
      onDragLeave: (e: React.DragEvent) => {
        e.preventDefault();
        chatDragDepthRef.current = Math.max(0, chatDragDepthRef.current - 1);
        if (chatDragDepthRef.current === 0) {
          setIsChatDragOver(false);
        }
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        resetChatDragState();
        if (!e.dataTransfer.files?.length) return;
        const images = Array.from(e.dataTransfer.files).filter(isImageAttachmentFile);
        if (images.length === 0) return;
        attachDroppedOrPastedImages(images, conv);
      },
    }),
    [attachDroppedOrPastedImages, canAcceptImageAttachment, resetChatDragState]
  );

  const handleComposerPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>, conv: ChatInterfaceProps["conversations"][number]) => {
      const clipboard = e.clipboardData;
      if (!clipboard) return;

      const imageFiles = getClipboardImageFiles(clipboard);
      if (imageFiles.length === 0) return;

      e.preventDefault();
      attachDroppedOrPastedImages(imageFiles, conv);
    },
    [attachDroppedOrPastedImages]
  );

  const startRecording = async () => {
    if (!activeConversation) return;

    if (activeConversation.state === "active") {
      toast({
        title: "⚠️ Agente ativo",
        description: "Pause o agente para gravar áudio.",
        variant: "destructive",
      });
      return;
    }

    if (activeConversation.state === "closed") {
      toast({
        title: "⚠️ Conversa encerrada",
        description: "Esta conversa foi encerrada",
        variant: "destructive",
      });
      return;
    }

    try {
      // 🎤 Gravação PTT compatível com Safari: WAV PCM vai pelo endpoint
      // sendWhatsAppAudio da Evolution e chega como áudio, não como anexo.
 console.log(` [${AUDIO_PTT_BUILD}] iniciando gravação PTT`);
      const recorder = createOpusRecorder();
      opusRecorderRef.current = recorder;
      stopIntentionRef.current = 'preview';

      const startedAt = Date.now();
      recordingStartedAtRef.current = startedAt;

      await recorder.start();

      setIsRecording(true);
      setRecordingTime(0);
      setRecordingState('recording');

      // Timer using setInterval (1s granularity, no rAF cleanup issues)
      const timerId = window.setInterval(() => {
        if (recordingStartedAtRef.current) {
          const elapsed = Math.round((Date.now() - startedAt) / 1000);
          setRecordingTime(elapsed);
        }
      }, 500);
      recordingTimerRef.current = timerId;
    } catch (error) {
 console.error("Error starting recording:", error);
      toast({
        title: "❌ Erro ao iniciar gravação",
        description: "Permissão de microfone negada ou não disponível",
        variant: "destructive"
      });
    }
  };

  const finalizeRecordingBlob = async () => {
    const recorder = opusRecorderRef.current;
    if (!recorder) return;
    try {
      const audioBlob = await recorder.stop();
      opusRecorderRef.current = null;
      if (stopIntentionRef.current === 'discard') return;
      if (!audioBlob || audioBlob.size === 0) {
        toast({
          title: "❌ Áudio vazio",
          description: "Não foi possível capturar o áudio. Tente novamente.",
          variant: "destructive",
        });
        return;
      }
      const blobMime = audioBlob.type || "audio/ogg";
      const ext = audioBlobExtension(blobMime);
      const fileName = `audio-${Date.now()}.${ext}`;
      const audioFile = new File([audioBlob], fileName, { type: blobMime });
 console.log(` [opus-recorder] gravação finalizada: ${fileName} (${audioBlob.size} bytes, ${audioBlob.type})`);

      if (!(await isWhatsAppPttAudioFile(audioFile))) {
 console.error(` [${AUDIO_PTT_BUILD}] gravação não gerou OGG/Opus`, {
          name: audioFile.name,
          type: audioFile.type,
          size: audioFile.size,
        });
        toast({
          title: "Áudio incompatível",
          description: "Atualize a página e grave novamente. O áudio de WhatsApp precisa estar em formato de voz compatível.",
          variant: "destructive",
        });
        setRecordingState('idle');
        setRecordingTime(0);
        setRecordedDuration(0);
        return;
      }

      const startedAt = recordingStartedAtRef.current ?? Date.now();
      const finalDuration = Math.round((Date.now() - startedAt) / 1000);
      setRecordedDuration(finalDuration);
      setRecordingTime(finalDuration);
      setSelectedFile(audioFile);
      setAudioPreviewUrl(URL.createObjectURL(audioBlob));
      setRecordingState('preview');
    } catch (err) {
 console.error("Error finalizing recording:", err);
      opusRecorderRef.current = null;
      toast({
        title: "❌ Erro ao finalizar gravação",
        description: "Não foi possível processar o áudio.",
        variant: "destructive",
      });
    }
  };


  const stopRecording = () => {
    if (opusRecorderRef.current && isRecording) {
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      stopIntentionRef.current = 'preview';
      setIsRecording(false);
      void finalizeRecordingBlob();
    }
  };

  const cancelRecording = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    stopIntentionRef.current = 'discard';
    if (opusRecorderRef.current) {
      try { opusRecorderRef.current.cancel(); } catch { /* ignore */ }
      opusRecorderRef.current = null;
    }
    setIsRecording(false);
    setRecordingState('idle');
    setRecordingTime(0);
    setRecordedDuration(0);
    setSelectedFile(null);
    if (audioPreviewUrl) {
      URL.revokeObjectURL(audioPreviewUrl);
    }
    setAudioPreviewUrl(null);
    recordingStartedAtRef.current = null;
  };

  const sendRecordedAudio = () => {
    if (selectedFile) {
      handleSendMedia();
      setRecordingState('idle');
      if (audioPreviewUrl) {
        URL.revokeObjectURL(audioPreviewUrl);
      }
      setAudioPreviewUrl(null);
      setRecordingTime(0);
    }
  };

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
      if (opusRecorderRef.current) {
        try { opusRecorderRef.current.cancel(); } catch { /* ignore */ }
        opusRecorderRef.current = null;
      }
    };
  }, []);


  const handleSendMedia = async () => {
    if (!selectedFile || !activeConversation) return;
    if (sendingRef.current) return;

    if (activeChannel === "instagram") {
      if (activeConversation.state !== "paused") {
        toast({
          title: "⚠️ Agente ativo",
          description: "Pause o agente para enviar imagens, áudio ou arquivos pelo Instagram.",
          variant: "destructive",
        });
        return;
      }

      const lastInbound = [...(messagesByChannel[activeChannel] || [])]
        .reverse()
        .find((m) => m.role === "assistant");
      if (lastInbound?.created_at) {
        const ageHours =
          (Date.now() - new Date(lastInbound.created_at).getTime()) / 3600000;
        if (ageHours > 24) {
          toast({
            title: "⚠️ Janela de 24h expirada",
            description:
              "O Instagram só permite responder até 24h após a última mensagem do contato. A tentativa pode falhar — se isso ocorrer, responda pelo aplicativo oficial.",
            variant: "destructive",
          });
        }
      }

      sendingRef.current = true;
      setIsLoading(true);
      try {
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result as string;
            resolve(base64.split(",")[1]);
          };
          reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
          reader.readAsDataURL(selectedFile);
        });

        const mediaType = getMediaType(selectedFile);
        if (mediaType === "video") {
          toast({
            title: "❌ Não disponível",
            description:
              "Envio de vídeo pelo Instagram neste painel ainda não está habilitado.",
            variant: "destructive",
          });
          return;
        }

        let mimetype = selectedFile.type;
        if (mimetype === "application/x-pdf") mimetype = "application/pdf";

        const { data, error } = await invokeWithRetry("send-instagram-media", {
          conversationId: activeConversation.conversation_id,
          mediaType: mediaType as "image" | "document" | "audio",
          media: base64Data,
          caption: input.trim() || undefined,
          mimetype,
          filename: selectedFile.name,
          memberId,
        });

        setQuotedMessage(null);

        if (error) {
          let errorMsg = "Erro desconhecido";
          if (data?.error) {
            errorMsg = data.error;
          } else if (typeof error === "object" && (error as any)?.context?.body) {
            try {
              const body =
                typeof (error as any).context.body === "string"
                  ? JSON.parse((error as any).context.body)
                  : (error as any).context.body;
              errorMsg = body?.error || (error as Error).message || errorMsg;
            } catch {
              errorMsg = (error as Error).message || errorMsg;
            }
          } else if ((error as Error).message) {
            errorMsg = (error as Error).message;
          }
          throw new Error(errorMsg);
        }

        if (data?.success === false) {
          toast({
            title: "Não foi possível enviar",
            description:
              (data as any)?.friendly_message ||
              (data as any)?.error ||
              "Tente novamente ou use o aplicativo do Instagram.",
            variant: "destructive",
          });
          setSelectedFile(null);
          setPreviewUrl(null);
          setInput("");
          setRecordedDuration(0);
          setRecordingState("idle");
          setAudioPreviewUrl(null);
          setRecordingTime(0);
          return;
        }

        await hydrateOutboundFromSendResponse(
          data as Record<string, unknown>,
          activeConversation.conversation_id,
          (rows) => applyServerMessagesRef.current?.(rows),
        );

        setSelectedFile(null);
        setPreviewUrl(null);
        setInput("");
        setRecordedDuration(0);
        setRecordingState("idle");
        setAudioPreviewUrl(null);
        setRecordingTime(0);
      } catch (error: any) {
 console.error("Error sending Instagram media:", error);
        toast({
          title: "Não foi possível enviar",
          description:
            error?.message || "Atualize a página e tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        sendingRef.current = false;
      }
      return;
    }

    sendingRef.current = true;
    setIsLoading(true);

    try {
      const mediaType = getMediaType(selectedFile);

      // Normalizar MIME type
      let mimetype = selectedFile.type;
      if (mimetype === 'application/x-pdf') mimetype = 'application/pdf';

      if (mediaType === 'audio') {
 console.log(` [${AUDIO_PTT_BUILD}] preparando envio PTT`, {
          name: selectedFile.name,
          type: mimetype,
          size: selectedFile.size,
        });
        if (!(await isWhatsAppPttAudioFile(selectedFile))) {
          toast({
            title: "Áudio incompatível",
            description: "Este envio precisa ser áudio de WhatsApp em formato de voz compatível. Atualize a página e grave novamente pelo microfone.",
            variant: "destructive",
          });
          return;
        }
        const lname = selectedFile.name.toLowerCase();
        const rawMime = (selectedFile.type || "").toLowerCase();
        if (rawMime.includes("ogg") || rawMime.includes("opus")) mimetype = "audio/ogg";
        else if (rawMime.includes("mp4") || rawMime.includes("m4a") || lname.endsWith(".m4a") || lname.endsWith(".mp4")) mimetype = "audio/mp4";
        else if (rawMime.includes("aac") || lname.endsWith(".aac")) mimetype = "audio/aac";
        else if (rawMime.includes("mpeg") || rawMime.includes("mp3") || lname.endsWith(".mp3")) mimetype = "audio/mpeg";
        else if (rawMime.includes("webm") || lname.endsWith(".webm")) mimetype = "audio/ogg";
        else if (lname.endsWith(".ogg") || lname.endsWith(".oga") || lname.endsWith(".opus")) mimetype = "audio/ogg";
        else if (lname.endsWith(".wav")) mimetype = "audio/wav";
        else mimetype = rawMime || "audio/ogg";
      }

      let mediaUrl: string | null = null;
      let objectKey: string | null = null;
      let storageProvider: 'minio' | 'supabase' | null = null;
      try {
        // Hash do conteúdo: permite reaproveitar um arquivo idêntico já enviado
        // neste workspace (sem novo upload e sem duplicar no armazenamento).
        let sha256: string | undefined;
        try {
          const buf = await selectedFile.arrayBuffer();
          const digest = await crypto.subtle.digest("SHA-256", buf);
          sha256 = Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
        } catch {
          sha256 = undefined;
        }

        const { data: presign, error: presignErr } = await supabase.functions.invoke(
          "presign-media-upload",
          {
            body: {
              conversation_id: activeConversation.conversation_id,
              media_type: mediaType,
              mime_type: mimetype,
              file_name: selectedFile.name,
              size: selectedFile.size,
              sha256,
            },
          }
        );
        if (!presignErr && (presign?.download_url || presign?.public_url)) {
          let uploaded = presign.already_exists === true;
          if (!uploaded && presign.upload_url) {
            const putResp = await fetch(presign.upload_url, {
              method: "PUT",
              headers: presign.headers || { "Content-Type": mimetype },
              body: selectedFile,
            });
            uploaded = putResp.ok;
            if (!putResp.ok) {
 console.warn("PUT MinIO falhou, caindo no fallback base64:", putResp.status);
            }
          }
          if (uploaded) {
            // Preferimos sempre a URL assinada: o bucket do MinIO não é público
            // (leitura anônima retorna 403), então public_url só serve como
            // último recurso caso o presign de download não venha.
            mediaUrl = presign.download_url || presign.public_url;
            objectKey = presign.object_key;
            storageProvider = 'minio';
          }
        } else if (presignErr) {
 console.warn("presign-media-upload indisponível, fallback base64:", presignErr);
        }
      } catch (presignFlowErr) {
 console.warn("Erro no fluxo MinIO, fallback base64:", presignFlowErr);
      }

      // Fallback: base64 (compat legado)
      let base64Data: string | undefined;
      if (!mediaUrl) {
        base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result as string;
            resolve(base64.split(',')[1]);
          };
          reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
          reader.readAsDataURL(selectedFile);
        });
      }

      const { data, error } = await invokeWithRetry("send-media-message", {
          conversationId: activeConversation.conversation_id,
          mediaType,
          ...(mediaUrl
            ? { mediaUrl, objectKey, storageProvider }
            : { media: base64Data }),
          caption: input.trim() || undefined,
          mimetype,
          filename: selectedFile.name,
          memberId,
          quotedMessage: quotedMessage ? {
            messageId: quotedMessage.messageId,
            content: quotedMessage.content,
            from: quotedMessage.from
          } : undefined
      });

      // Limpar quote após envio
      setQuotedMessage(null);

      if (error) {
        // Extract error message from edge function response
        let errorMsg = "Erro desconhecido";
        if (data?.error) {
          errorMsg = data.error;
        } else if (typeof error === 'object' && error?.context?.body) {
          try {
            const body = typeof error.context.body === 'string' ? JSON.parse(error.context.body) : error.context.body;
            errorMsg = body?.error || error.message || errorMsg;
          } catch {
            errorMsg = error.message || errorMsg;
          }
        } else if (error.message) {
          errorMsg = error.message;
        }
        throw new Error(errorMsg);
      }

      if (data?.success === false) {
        const failureReason = (data as any)?.failure_reason;
        const invalidNumber = (data as any)?.invalid_number;
        const friendly = (data as any)?.friendly_message;

        if (failureReason === "number_not_on_whatsapp") {
          const formatted = invalidNumber ? `+${invalidNumber}` : "(número não informado)";
          sonnerToast.error("Número sem WhatsApp", {
            description: `${formatted} não possui conta WhatsApp ativa. Verifique o telefone do lead.`,
            duration: Infinity,
            action: {
              label: "Editar contato",
              onClick: () => {
                window.dispatchEvent(new CustomEvent("open-lead-details", { detail: { leadId } }));
              },
            },
          });
        } else if (friendly) {
          toast({
            title: "Não foi possível enviar",
            description: friendly,
            variant: "destructive",
          });
        }

        setSelectedFile(null);
        setPreviewUrl(null);
        setInput("");
        setRecordedDuration(0);
        setRecordingState('idle');
        setAudioPreviewUrl(null);
        setRecordingTime(0);
        return;
      }

      await hydrateOutboundFromSendResponse(
        data as Record<string, unknown>,
        activeConversation.conversation_id,
        (rows) => applyServerMessagesRef.current?.(rows),
      );

      setSelectedFile(null);
      setPreviewUrl(null);
      setInput("");
      setRecordedDuration(0);
      setRecordingState('idle');
      setAudioPreviewUrl(null);
      setRecordingTime(0);
    } catch (error: any) {
 console.error("Error sending media:", error);
      toast({
        title: "Não foi possível enviar",
        description: error?.message || "Atualize a página e tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      sendingRef.current = false;
    }
  };

  const handleEnhanceMessage = async () => {
    if (!input.trim() || isEnhancing) return;
    setIsEnhancing(true);
    setEnhancedText(null);
    try {
      const { data, error } = await invokeWithRetry("enhance-message", {
        text: input.trim(),
        workspace_id: workspaceId,
        conversation_id: activeConversation?.conversation_id,
      });
      if (error) throw error;
      if (data?.enhanced) {
        setEnhancedText(data.enhanced);
      } else {
        toast({ title: "Erro", description: "Não foi possível melhorar a mensagem.", variant: "destructive" });
      }
    } catch (err: any) {
 console.error("Enhance error:", err);
      toast({ title: "Erro", description: "Falha ao melhorar mensagem. Tente novamente.", variant: "destructive" });
    } finally {
      setIsEnhancing(false);
    }
  };

  const sendQueuedTextMessage = useCallback(async (item: QueuedTextMessage) => {
    const conv = conversations.find((c) => c.conversation_id === item.conversationId);
    if (!conv) return;

    if (conv.state === "active") {
      toast({
        title: "⚠️ Agente ativo",
        description: "Pause o agente primeiro para responder manualmente",
        variant: "destructive",
      });
      return;
    }

    if (conv.state === "closed") {
      toast({
        title: "⚠️ Conversa encerrada",
        description: "Esta conversa foi encerrada",
        variant: "destructive",
      });
      return;
    }

    const channelMessages = messagesByChannel[item.channel] || [];

    if (item.channel === "instagram" || item.channel === "messenger") {
      const lastInbound = [...channelMessages].reverse().find((m) => m.role === "assistant");
      if (lastInbound?.created_at) {
        const ageHours = (Date.now() - new Date(lastInbound.created_at).getTime()) / 3600000;
        if (ageHours > 24) {
          toast({
            title: "⚠️ Janela de 24h expirada",
            description:
              "O Instagram/Messenger só permite responder até 24h após a última mensagem do contato. A tentativa pode falhar — se isso ocorrer, responda pelo aplicativo oficial.",
            variant: "destructive",
          });
        }
      }
    }

    if (
      item.channel === "whatsapp" &&
      item.integrationId &&
      cloudIntegrationIds.has(item.integrationId) &&
      cloudWindowExpiresAt &&
      new Date(cloudWindowExpiresAt).getTime() <= Date.now()
    ) {
      toast({
        title: "⚠️ Janela de 24h expirada",
        description:
          "O WhatsApp Cloud só permite mensagens de sessão dentro de 24h após a última mensagem do contato. Use um template aprovado para reabrir a conversa.",
        variant: "destructive",
      });
    }

    const functionName =
      item.channel === "instagram"
        ? "send-instagram-message"
        : item.channel === "messenger"
          ? "send-messenger-message"
          : "send-manual-message";

    const { data: sendData, error: sendError } = await invokeWithRetry(functionName, {
      conversationId: item.conversationId,
      message: item.text,
      channel: item.channel,
      memberId,
      clientMessageId: item.clientMessageId,
      quotedMessage: item.quotedMessage
        ? {
            messageId: item.quotedMessage.messageId,
            content: item.quotedMessage.content,
            from: item.quotedMessage.from,
          }
        : undefined,
    });

    if (sendError) throw sendError;

    if (sendData && sendData.success === false) {
      const failureReason = (sendData as any)?.failure_reason;
      const invalidNumber = (sendData as any)?.invalid_number;
      const friendly = (sendData as any)?.friendly_message;

      if (failureReason === "number_not_on_whatsapp") {
        const formatted = invalidNumber ? `+${invalidNumber}` : "(número não informado)";
        sonnerToast.error("Número sem WhatsApp", {
          description: `${formatted} não possui conta WhatsApp ativa. Verifique o telefone do lead.`,
          duration: Infinity,
          action: {
            label: "Editar contato",
            onClick: () => {
              window.dispatchEvent(new CustomEvent("open-lead-details", { detail: { leadId } }));
            },
          },
        });
      } else if (friendly) {
        toast({
          title: "Não foi possível enviar",
          description: friendly,
          variant: "destructive",
        });
      }
      return;
    }

    await hydrateOutboundFromSendResponse(
      sendData as Record<string, unknown>,
      item.conversationId,
      (rows) => applyServerMessagesRef.current?.(rows),
    );
  }, [
    conversations,
    messagesByChannel,
    cloudIntegrationIds,
    cloudWindowExpiresAt,
    memberId,
    leadId,
    toast,
  ]);

  const processSendQueue = useCallback(async (conversationId: string) => {
    if (sendQueueProcessingRef.current.has(conversationId)) return;
    sendQueueProcessingRef.current.add(conversationId);
    syncActivePendingSendCount();

    try {
      while (true) {
        const queue = sendQueueRef.current.get(conversationId);
        if (!queue || queue.length === 0) break;

        const item = queue.shift()!;
        syncActivePendingSendCount();

        try {
          await sendQueuedTextMessage(item);
        } catch (error: any) {
 console.error("Error sending manual message:", error);
          toast({
            title: "Não foi possível enviar",
            description: "Atualize a página e tente novamente.",
            variant: "destructive",
          });
        }
      }
    } finally {
      sendQueueProcessingRef.current.delete(conversationId);
      syncActivePendingSendCount();
    }
  }, [sendQueuedTextMessage, syncActivePendingSendCount, toast]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !activeConversation || selectedFile) return;

    if (activeConversation.state === "active") {
      toast({
        title: "⚠️ Agente ativo",
        description: "Pause o agente primeiro para responder manualmente",
        variant: "destructive",
      });
      return;
    }

    if (activeConversation.state === "closed") {
      toast({
        title: "⚠️ Conversa encerrada",
        description: "Esta conversa foi encerrada",
        variant: "destructive",
      });
      return;
    }

    const item: QueuedTextMessage = {
      conversationId: activeConversation.conversation_id,
      channel: activeChannel,
      text: input.trim(),
      clientMessageId: crypto.randomUUID(),
      quotedMessage: quotedMessage,
      integrationId: activeConversation.integration_id,
    };

    setInput("");
    setQuotedMessage(null);

    const queue = sendQueueRef.current.get(item.conversationId) ?? [];
    queue.push(item);
    sendQueueRef.current.set(item.conversationId, queue);
    syncActivePendingSendCount();

    void processSendQueue(item.conversationId);
  };

  const activeChannelMessages = messagesByChannel[activeChannel] || [];
  // Prévia provisória (read model) nunca pode coexistir com a mensagem real já carregada,
  // independentemente de qual caminho (realtime, sync, carga inicial) trouxe a real.
  const currentMessages = removeSupersededHubPreviews(activeChannelMessages, activeChannelMessages).filter(
    (m: any) => {
      const body = m.content?.trim();
      if (body?.toLowerCase() === '[template]') return false;
      const visualTypes = ['story_mention', 'share', 'image', 'video', 'audio', 'document', 'product_inquiry'];
      const hasProductCtx = !!(m.metadata?.product || m.metadata?.quoted?.product);
      if (!body && !visualTypes.includes(m.metadata?.type) && !hasProductCtx) return false;
      return true;
    }
  );

  // Função auxiliar para determinar quem enviou a mensagem citada
  const getQuotedMessageSender = (quotedFrom: string | undefined) => {
    if (!quotedFrom) return "Desconhecido";
    if (quotedFrom === "assistant") return "Você";
    // Se contém @s.whatsapp.net ou parece ser um número, é o lead
    if (quotedFrom.includes("@") || /^\d+$/.test(quotedFrom)) {
      return leadName;
    }
    return quotedFrom;
  };

  // Formata preço do catálogo (vem em string ou number, com currency opcional)
  const formatCatalogPrice = (price?: string | number, currency?: string) => {
    if (price === undefined || price === null || price === '') return null;
    const num = typeof price === 'number' ? price : Number(String(price).replace(/[^\d.,-]/g, '').replace(',', '.'));
    if (Number.isFinite(num)) {
      const cur = (currency || 'BRL').toUpperCase();
      try {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: cur }).format(num);
      } catch {
        return `${cur} ${num.toFixed(2)}`;
      }
    }
    return `${currency || ''} ${price}`.trim();
  };

  // Card visual para um produto do catálogo (inquiry direta ou citado em reply)
  const CatalogProductCard = ({
    product,
    label,
    variant = 'compact',
  }: {
    product: NonNullable<NonNullable<Message['metadata']>['product']>;
    label?: string;
    variant?: 'compact' | 'full';
  }) => {
    const priceLabel = formatCatalogPrice(product.price, product.currency);
    const imgSize = variant === 'full' ? 'w-20 h-20' : 'w-16 h-16';
    return (
      <div className="rounded-lg border border-border/60 bg-background/40 p-2 mb-2 max-w-[340px]">
        <div className="flex gap-2">
          <div className="flex flex-col min-w-0 flex-1 justify-center">
            <div className="flex items-start gap-1.5 min-w-0">
              <Store className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
                {product.title || 'Produto do catálogo'}
              </span>
            </div>
            {product.description && (
              <span className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {product.description}
              </span>
            )}
            {priceLabel && (
              <span className="text-xs font-medium text-foreground/90 mt-1">{priceLabel}</span>
            )}
            {product.url && (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-primary hover:underline truncate inline-flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" /> Ver no catálogo
              </a>
            )}
          </div>
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.title || 'Produto'}
              className={`${imgSize} rounded-md object-cover flex-shrink-0`}
            />
          ) : (
            <div className={`${imgSize} rounded-md bg-muted flex items-center justify-center flex-shrink-0`}>
              <ShoppingBag className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
        </div>
        {label && (
          <span className="block text-[10px] text-muted-foreground mt-1.5">{label}</span>
        )}
      </div>
    );
  };

  // Renderiza pílula de log de atividade (IA toggle, atribuição, encerramento, estado inicial, etc.)
  const renderActivityLogPill = (log: any) => {
    const isAssignment = log.action_type === 'lead_assigned' || log.action_type === 'lead_unassigned';
    const isClose = log.action_type === 'conversation_closed';
    const isReopen = log.action_type === 'conversation_reopened';
    const isInitial = log.action_type === 'state_initial_active' || log.action_type === 'state_initial_paused';
    const IconComponent = isClose ? CheckCircle : isReopen ? RotateCcw : isAssignment ? UserPlus : Bot;
    const displayName = log.member?.display_name || (log.metadata as any)?.member_name;
    let logText = '';
    if (log.action_type === 'ai_enabled') {
      const meta = (log.metadata as Record<string, unknown> | undefined) ?? {};
      if (meta.reason === "agent_routed") {
        logText =
          (typeof meta.reason_label === "string" && meta.reason_label) ||
          formatAgentTransferLabel(meta);
      } else {
        logText = `IA ativada${displayName ? ` por ${displayName}` : ''}`;
      }
    } else if (log.action_type === 'ai_disabled') {
      const reasonLabel = (log.metadata as any)?.reason_label;
      if (reasonLabel) {
        logText = `IA desativada — ${displayName ? `${displayName} ` : ''}${reasonLabel}`;
      } else {
        logText = `IA desativada${displayName ? ` por ${displayName}` : ''}`;
      }
    } else if (log.action_type === 'state_initial_active') {
      logText = 'Estado inicial registrado: IA ativa';
    } else if (log.action_type === 'state_initial_paused') {
      logText = 'Estado inicial registrado: IA pausada';
    } else if (log.action_type === 'lead_assigned') {
      const assignedName = (log.metadata as any)?.assigned_member_name || 'membro';
      logText = `Lead atribuído a ${assignedName}${displayName ? ` por ${displayName}` : ''}`;
    } else if (log.action_type === 'lead_unassigned') {
      logText = `Atribuição removida${displayName ? ` por ${displayName}` : ''}`;
    } else if (isClose) {
      logText = `Atendimento encerrado${displayName ? ` por ${displayName}` : ''}`;
    } else if (isReopen) {
      logText = `Atendimento reaberto${displayName ? ` por ${displayName}` : ''}`;
    }
    return (
      <div key={log.id} className={`flex items-center gap-2 text-xs justify-center my-2 ${isInitial ? 'text-muted-foreground/70 italic' : 'text-muted-foreground'}`}>
        <IconComponent className="w-3 h-3" />
        <span>
          {logText}
          {log.created_at ? ` - ${formatChatActivityLogTime(log.created_at)}` : ""}
        </span>
      </div>
    );
  };

  const renderComposerOverflowItems = (conv: ChatInterfaceProps["conversations"][number]) => (
    <>
      <DropdownMenuItem
        onSelect={() => {
          setTimeout(() => layoutsPopoverRef.current?.open(), 0);
        }}
        disabled={isLoading || conv.state === "closed"}
      >
        <LayoutTemplate className="mr-2 h-4 w-4" />
        Modelos e mídia
      </DropdownMenuItem>
      {conv.integration_id && supportsInteractiveOutbound(integrationProviders[conv.integration_id]) && (
        <DropdownMenuItem
          onSelect={() => {
            setTimeout(() => setInteractiveDialogOpen(true), 0);
          }}
          disabled={isLoading || conv.state === "active" || conv.state === "closed"}
        >
          <LayoutList className="mr-2 h-4 w-4" />
          {getWhatsAppProviderCapabilities(integrationProviders[conv.integration_id])?.carousel
            ? "Botões, lista, enquete ou carrossel"
            : "Botões, lista ou enquete"}
        </DropdownMenuItem>
      )}
      {conv.channel === "whatsapp" &&
        conv.integration_id &&
        (integrationProviders[conv.integration_id] === "whatsapp_cloud" ||
          integrationProviders[conv.integration_id] === "whatsapp_uazapi") && (
        <DropdownMenuItem
          onSelect={() => {
            setTimeout(() => setContactDialogOpen(true), 0);
          }}
          disabled={isLoading || conv.state === "active" || conv.state === "closed"}
        >
          <Contact className="mr-2 h-4 w-4" />
          Enviar contato
        </DropdownMenuItem>
      )}
      {conv.integration_id && cloudIntegrationIds.has(conv.integration_id) && (
        <>
          <DropdownMenuItem
            onSelect={() => {
              setTimeout(() => setTemplateDialogOpen(true), 0);
            }}
            disabled={isLoading || conv.state === "active" || conv.state === "closed"}
          >
            <FileText className="mr-2 h-4 w-4" />
            Template (24h+)
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuItem
        onSelect={() => {
          setTimeout(() => fileInputRef.current?.click(), 0);
        }}
        disabled={
          isLoading ||
          conv.state === "active" ||
          conv.state === "closed" ||
          (conv.channel === "instagram" && conv.state !== "paused")
        }
      >
        <Paperclip className="mr-2 h-4 w-4" />
        Anexar arquivo
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => {
          setTimeout(() => setProductLookupOpen(true), 0);
        }}
      >
        <ShoppingBag className="mr-2 h-4 w-4" />
        Consultar produto/serviço
      </DropdownMenuItem>
      {onLeadRecovery && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setTimeout(() => onLeadRecovery(conv.conversation_id), 0);
          }}
          disabled={isLeadRecovering}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {isLeadRecovering
            ? "Analisando..."
            : leadRecoveryPlanAllowed
              ? "Recuperar Lead"
              : "Recuperar Lead (Plus)"}
        </DropdownMenuItem>
      )}
      {conv.state === "paused" && input.trim().length > 0 && (
        <DropdownMenuItem
          onSelect={() => {
            void handleEnhanceMessage();
          }}
          disabled={isEnhancing}
        >
          <Sparkles className={`mr-2 h-4 w-4 ${isEnhancing ? "animate-spin" : ""}`} />
          Melhorar com IA
        </DropdownMenuItem>
      )}
    </>
  );

  return (
    <div className="flex flex-col h-full w-full min-h-0 overflow-hidden">
      {/* Abas de Canais — só exibe quando há mais de uma conversa (ex.: WhatsApp + Instagram) */}
      <Tabs value={activeChannel} onValueChange={setActiveChannel} className="flex-1 flex flex-col min-h-0">
        {conversations.length > 1 && (
          <div className="flex-shrink-0 border-b bg-muted/30 px-2 sm:px-4">
            <TabsList className="bg-transparent h-12 gap-1">
              {conversations.map(conv => {
                const Icon = CHANNEL_ICONS[conv.channel as keyof typeof CHANNEL_ICONS] || Phone;
                return (
                  <TabsTrigger
                    key={conv.channel}
                    value={conv.channel}
                    className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="capitalize hidden sm:inline">{conv.channel}</span>
                    {conv.state === 'paused' && (
                      <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-orange-500 text-orange-600">P</Badge>
                    )}
                    {conv.state === 'closed' && (
                      <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-gray-500 text-gray-600">F</Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        )}

        {/* Conteúdo de cada canal */}
        {conversations.map(conv => {
          const chatDragHandlers = createChatDragHandlers(conv);
          return (
          <TabsContent
            key={conv.channel}
            value={conv.channel}
            className="relative flex-1 flex flex-col min-h-0 mt-0"
            {...chatDragHandlers}
          >
            {isChatDragOver && conv.channel === activeChannel && (
              <div
                className={cn(
                  "pointer-events-none absolute inset-0 z-40 flex items-center justify-center border-2 border-dashed rounded-lg m-1 sm:m-2",
                  canAcceptImageAttachment(conv)
                    ? "border-primary/60 bg-primary/10"
                    : "border-muted-foreground/40 bg-muted/40"
                )}
              >
                <div className="flex flex-col items-center gap-2 rounded-lg bg-background/90 px-4 py-3 shadow-sm">
                  <Image className="h-8 w-8 text-primary" />
                  <p className="text-sm font-medium">
                    {canAcceptImageAttachment(conv)
                      ? "Solte a imagem para anexar"
                      : "Não é possível anexar nesta conversa"}
                  </p>
                </div>
              </div>
            )}
            {/* Área de mensagens */}
            <div
              ref={conv.channel === activeChannel ? messagesScrollContainerRef : undefined}
              className="flex-1 overflow-y-auto p-2 sm:p-3 lg:p-4 min-h-0"
            >
              <div
                className={cn(
                  "space-y-3 sm:space-y-4 transition-opacity duration-75",
                  conv.channel === activeChannel && !messagesViewportReady && !messagesLoading
                    ? "opacity-0"
                    : "opacity-100",
                )}
              >
              <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-2 py-1 inline-block">
                Fonte de entrada e envio: {accountOriginLabel || conv.originLabel || conv.channel}
              </div>
              {/* Load older messages button */}
              {hasMoreMessages && (
                <div className="text-center py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadOlderMessages}
                    disabled={loadingMore}
                    className="text-xs text-muted-foreground"
                  >
                    {loadingMore ? "Carregando..." : "↑ Carregar mensagens anteriores"}
                  </Button>
                </div>
              )}
              {messagesLoading && <ChatMessagesSkeleton />}
              {messagesError && !messagesLoading && (
                <div className="text-center py-4 px-4">
                  <div className="inline-flex items-center gap-2 rounded-md bg-destructive/10 text-destructive text-sm px-3 py-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{messagesError}</span>
                  </div>
                </div>
              )}
              {currentMessages.length === 0 && !messagesLoading && !messagesError && (
                <div className="text-center text-muted-foreground py-6 sm:py-8">
                  <Bot className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base px-4">Olá! Sou seu assistente de vendas. Como posso ajudar?</p>
                </div>
              )}

              {currentMessages.map((message, index) => {
                const clientProfilePic = getClientProfilePicture();
                const isUserMessage = message.role === "user";
                const isManualPhoneMessage = isUserMessage && isOutgoingFromManualPhone(message);
                const isAiSentMessage = isUserMessage && isOutgoingFromAi(message);
                const isAudioMessage = message.metadata?.type === "audio";
                const messageTextLayout = getMessageTextLayout(message);
                const renderMessageTimestamp = (layout: "inline" | "block") => {
                  if (!message.created_at) return null;
                  const audioTranscriptionSlot =
                    message.metadata?.type === "audio" && message.id
                      ? (() => {
                          const transcription = getAudioTranscription(message);
                          if (!transcription) return null;
                          const isExpanded = audioTranscriptionExpandedIds.has(message.id);
                          return (
                            <>
                              <span className="opacity-50" aria-hidden>
                                ·
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleAudioTranscription(message.id!)}
                                className="underline-offset-2 hover:underline"
                              >
                                {isExpanded ? "Ocultar transcrição" : "Ver transcrição"}
                              </button>
                            </>
                          );
                        })()
                      : null;

                  return (
                    <MessageTimestampMeta
                      createdAt={message.created_at}
                      isUserMessage={isUserMessage}
                      deliveryStatus={message.delivery_status}
                      friendlyMessage={(message.metadata as { friendly_message?: string } | undefined)?.friendly_message}
                      layout={layout}
                      audioTranscriptionSlot={audioTranscriptionSlot}
                    />
                  );
                };
                const wrapInlineMessageText = (node: React.ReactNode) => {
                  if (
                    conv.channel !== "whatsapp" ||
                    messageTextLayout !== "inline" ||
                    !message.created_at
                  ) {
                    return node;
                  }
                  return (
                    <ChatMessageTextRow meta={renderMessageTimestamp("inline")}>
                      {node}
                    </ChatMessageTextRow>
                  );
                };


                // Marcador de data entre mensagens
                const prevMsg = currentMessages[index - 1];
                const showDateSeparator = shouldShowChatDateSeparator(
                  index,
                  message.created_at,
                  prevMsg?.created_at,
                );
                
                // Verificar se há log de atividade entre essa mensagem e a próxima
                const currentMsgTime = message.created_at ? new Date(message.created_at).getTime() : 0;
                const nextMsg = currentMessages[index + 1];
                const nextMsgTime = nextMsg?.created_at ? new Date(nextMsg.created_at).getTime() : Infinity;
                
                // Filtrar logs entre esta mensagem e a próxima. Na última mensagem, nextMsgTime é
                // Infinity — incluiría todos os logs posteriores aqui e de novo no bloco logsAfterLast (duplicado).
                const logsBetween = activityLogs.filter(log => {
                  const logTime = new Date(log.created_at).getTime();
                  if (nextMsg === undefined) return false;
                  return logTime > currentMsgTime && logTime < nextMsgTime;
                });

                return (
                  <React.Fragment key={message.id || index}>
                  {showDateSeparator && message.created_at && (
                    <div className="flex justify-center my-2">
                      <span className="bg-muted/80 text-muted-foreground text-[11px] px-3 py-1 rounded-lg shadow-sm">
                        {formatChatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex min-w-0 gap-2 sm:gap-3 items-start ${isUserMessage ? "justify-end" : "justify-start"}`}
                    onClick={() => {
                      if (selectionMode && message.id) {
                        toggleMessageSelection(message.id);
                      }
                    }}
                  >
                    {/* Checkbox no modo seleção - lado esquerdo */}
                    {selectionMode && (
                      <div className="flex items-center pt-2">
                        <Checkbox
                          checked={selectedMessageIds.has(message.id || "")}
                          onCheckedChange={() => message.id && toggleMessageSelection(message.id)}
                          className="data-[state=checked]:bg-primary"
                        />
                      </div>
                    )}
                    {!isUserMessage && !selectionMode && (
                      <Avatar className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0">
                        {clientProfilePic ? (
                          <AvatarImage src={clientProfilePic} alt={leadName} referrerPolicy="no-referrer" />
                        ) : null}
                        <AvatarFallback className="bg-primary/10">
                          <User className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className={cn(
                        "group relative flex min-w-0 flex-col gap-1",
                        conv.channel === "whatsapp"
                          ? cn(
                              "w-fit max-w-[75%] shrink-0",
                              isUserMessage ? "items-end ml-auto" : "items-start",
                            )
                          : "max-w-[min(100%,_85%)] sm:max-w-[min(100%,_80%)]",
                      )}
                      onMouseEnter={() => !selectionMode && setHoveredMessageIndex(index)}
                      onMouseLeave={() => {
                        if (reactionPickerIndex !== index && dropdownOpenIndex !== index) {
                          setHoveredMessageIndex(null);
                        }
                      }}
                    >
                      {/* Hover actions: responder, reação, encaminhar, menu */}
                      {!selectionMode && !message.deleted_at && (hoveredMessageIndex === index || dropdownOpenIndex === index) && message.id && (
                        <div className={`absolute ${isUserMessage ? "left-0 -translate-x-full" : "right-0 translate-x-full"} top-1/2 -translate-y-1/2 px-1 z-10 flex gap-1`}>
                          {/* Botão de responder */}
                          {(conv.channel === 'whatsapp' || conv.channel === 'instagram') &&
                            (message.metadata?.message_id || message.provider_message_id) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-7 h-7 rounded-full bg-background shadow-md hover:bg-accent border"
                              onClick={() => {
                                const isCloudConv =
                                  conv.channel === "whatsapp" &&
                                  !!conv.integration_id &&
                                  cloudIntegrationIds.has(conv.integration_id);
                                setQuotedMessage({
                                  id: message.id!,
                                  messageId: message.id!,
                                  content: message.content.substring(0, 100),
                                  from: message.role === "assistant" ? leadMetadata?.phone || "user" : "assistant",
                                  ...(isCloudConv && message.provider_message_id
                                    ? { cloudWamid: message.provider_message_id }
                                    : {}),
                                });
                              }}
                              title="Responder"
                            >
                              <Reply className="w-3.5 h-3.5" />
                            </Button>
                          )}

                          {/* Botão de reação */}
                          {(conv.channel === 'whatsapp' || conv.channel === 'instagram') && (
                            <Popover 
                              open={reactionPickerIndex === index} 
                              onOpenChange={(open) => {
                                setReactionPickerIndex(open ? index : null);
                                if (!open) setHoveredMessageIndex(null);
                              }}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 rounded-full bg-background shadow-md hover:bg-accent border"
                                >
                                  <Smile className="w-3.5 h-3.5" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-2" side={isUserMessage ? "left" : "right"}>
                                <div className="flex gap-1">
                                  {QUICK_REACTIONS.map(emoji => (
                                    <button
                                      key={emoji}
                                      className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-muted"
                                      onClick={() => handleSendReaction(message.id!, emoji)}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}

                          {/* Encaminhar (mesma conexão da conversa) */}
                          {conv.channel === "whatsapp" &&
                            conv.integration_id &&
                            canForwardWhatsAppMessage(message.metadata, message.content, message.deleted_at) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-7 h-7 rounded-full bg-background shadow-md hover:bg-accent border"
                              onClick={() => setForwardMessage(message)}
                              title="Encaminhar"
                            >
                              <Forward className="w-3.5 h-3.5" />
                            </Button>
                          )}

                          {/* Dropdown menu - estilo WhatsApp */}
                          <DropdownMenu open={dropdownOpenIndex === index} onOpenChange={(open) => setDropdownOpenIndex(open ? index : null)}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 rounded-full bg-background shadow-md hover:bg-accent border"
                              >
                                <ChevronDown className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isUserMessage ? "end" : "start"} className="min-w-[160px]">
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectionMode(true);
                                  setSelectedMessageIds(new Set([message.id!]));
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Selecionar
                              </DropdownMenuItem>
                              {message.id && !message.deleted_at && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setAttachToTaskMessage(message);
                                    setDropdownOpenIndex(null);
                                  }}
                                >
                                  <ListTodo className="w-4 h-4 mr-2" />
                                  Anexar a tarefa
                                </DropdownMenuItem>
                              )}
                              {activeChannel === "whatsapp" && ((message as any).provider_message_id || (message.metadata as any)?.message_id) && (message as any).role === "user" && (!(message.metadata as any)?.type || (message.metadata as any)?.type === "text") && !message.deleted_at && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditingMessage({ id: message.id!, body: message.content || "" });
                                    setEditingText(message.content || "");
                                    setDropdownOpenIndex(null);
                                  }}
                                >
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {(activeChannel === "whatsapp" || activeChannel === "instagram" || activeChannel === "messenger") && ((message as any).provider_message_id || (message.metadata as any)?.message_id) && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => handleDeleteForEveryone(message.id!)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Apagar para todos
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteMessages([message.id!])}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Apagar para mim
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}

                      <div
                        className={cn(
                          "min-w-0 rounded-lg sm:rounded-lg",
                          conv.channel === "whatsapp"
                            ? "w-fit max-w-full px-[7px] py-[6px] text-sm leading-snug"
                            : cn(
                                "px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base",
                                isAudioMessage ? "w-fit max-w-[min(100%,320px)]" : "w-full max-w-full",
                              ),
                          message.role === "user"
                            ? "chat-bubble-user rounded-tr-none sm:rounded-tr-lg"
                            : "bg-muted rounded-tl-none sm:rounded-tl-lg",
                          message.deleted_at && "italic opacity-80",
                          isAudioMessage &&
                            conv.channel === "whatsapp" &&
                            "max-w-[min(100%,320px)]",
                        )}
                      >
                        {message.deleted_at ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Ban className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>
                              {message.deleted_by_member?.display_name
                                ? `Mensagem apagada por ${message.deleted_by_member.display_name}`
                                : "Mensagem apagada"}
                            </span>
                          </div>
                        ) : (<>
                        {/* Marca remetente: celular físico, IA ou membro (painel) */}
                        {isManualPhoneMessage && (
                          <div className="mb-1 flex items-center gap-1">
                            <Smartphone className="h-3 w-3 flex-shrink-0 text-primary-foreground/80" aria-hidden />
                            <span className="text-xs font-semibold text-primary-foreground/80">
                              {getManualPhoneSenderLabel()}
                            </span>
                          </div>
                        )}
                        {isAiSentMessage && (
                          <div className="mb-1 flex items-center gap-1">
                            <Bot className="h-3 w-3 flex-shrink-0 text-primary-foreground/80" aria-hidden />
                            <span className="text-xs font-semibold text-primary-foreground/80">
                              {getAiSenderLabel(message)}
                            </span>
                          </div>
                        )}
                        {isUserMessage && message.sent_by_member?.display_name && (
                          <p className="text-xs mb-1">
                            <span className="inline-flex items-center gap-1">
                              <UserRound
                                className="h-3 w-3 shrink-0"
                                style={{ color: "#28292B" }}
                                strokeWidth={2}
                                aria-hidden
                              />
                              <span className="font-medium" style={{ color: "#28292B" }}>
                                {message.sent_by_member.display_name}
                              </span>
                            </span>
                            {(accountOriginLabel || (message.metadata as any)?.instance) && (
                              <span className="font-semibold text-primary-foreground/80 opacity-80">
                                {" "}
                                • via {accountOriginLabel || (message.metadata as any).instance}
                              </span>
                            )}
                          </p>
                        )}
                        {/* Story reaction banner */}
                        {message.metadata?.is_story_reply && (
                          <div className={`border-l-4 pl-3 pr-2 py-2.5 mb-2 rounded-r-md min-w-0 ${
                            isUserMessage 
                              ? "border-chat-bubble-user-fg/50 chat-bubble-user-fg/10" 
                              : "border-purple-400 bg-black/5 dark:bg-white/5"
                          }`}>
                            <div className="flex items-center gap-3 min-w-0">
                              {message.metadata.story_url && (
                                <a href={message.metadata.story_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                  <img
                                    src={message.metadata.story_url}
                                    alt="Story"
                                    className="w-12 h-12 rounded-lg object-cover"
                                    loading="lazy"
                                  />
                                </a>
                              )}
                              <div className="min-w-0 flex-1">
                                <span className={`text-xs font-semibold whitespace-normal break-words block ${
                                  isUserMessage ? "chat-bubble-user-fg/90" : "text-purple-600 dark:text-purple-400"
                                }`}>
                                  {/* Semantic rendering: use persisted classification, fallback to legacy heuristic */}
                                  {message.metadata?.normalized_event_type === 'REACTION'
                                    ? "💜 Reagiu à mensagem"
                                    : message.metadata?.normalized_message_subtype === 'STORY_REPLY'
                                      ? (message.metadata?.emoji_only
                                        ? "📸 Reagiu ao seu Story"
                                        : "💬 Respondeu ao seu Story")
                                      : (message.content && message.content.trim() && !(/^\p{Emoji}$/u.test(message.content.trim())))
                                        ? "💬 Respondeu ao seu Story"
                                        : "📸 Reagiu ao seu Story"
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Click-to-WhatsApp: resposta a anúncio Instagram/Facebook */}
                        {(message.metadata as any)?.ad_reply && (
                          <AdReplyCard adReply={(message.metadata as any).ad_reply} />
                        )}



                        {/* Produto do catálogo citado em resposta (v2) */}
                        {message.metadata?.quoted?.kind === 'product' && message.metadata.quoted.product && (
                          <CatalogProductCard
                            product={message.metadata.quoted.product}
                            label="Respondendo a este produto"
                            variant="compact"
                          />
                        )}

                        {/* Texto/mídia citados (v2) */}
                        {message.metadata?.quoted && message.metadata.quoted.kind !== 'product' && (message.metadata.quoted.text || message.metadata.quoted.caption || message.metadata.quoted.mediaType) && (
                          <div className={`border-l-4 pl-2 py-1 mb-2 rounded text-xs ${
                            isUserMessage
                              ? "border-chat-bubble-user-fg/50 chat-bubble-user-fg/10"
                              : "border-orange-400 bg-black/5 dark:bg-white/5"
                          }`}>
                            <span className={`font-semibold block ${
                              isUserMessage ? "chat-bubble-user-fg/80" : "text-orange-500"
                            }`}>
                              Em resposta a
                            </span>
                            <span className="opacity-80 line-clamp-2">
                              {message.metadata.quoted.text
                                || message.metadata.quoted.caption
                                || `[${message.metadata.quoted.mediaType}]`}
                            </span>
                          </div>
                        )}

                        {/* Produto enviado pelo lead (inquiry direta) */}
                        {message.metadata?.product && (
                          <CatalogProductCard
                            product={message.metadata.product}
                            label="Item do catálogo"
                            variant="full"
                          />
                        )}

                        {/* Mensagem citada (formato legado) */}
                        {message.metadata?.quoted_message_body && !message.metadata?.quoted && (
                          <div className={`border-l-4 pl-2 py-1 mb-2 rounded text-xs ${
                            isUserMessage 
                              ? "border-chat-bubble-user-fg/50 chat-bubble-user-fg/10" 
                              : "border-orange-400 bg-black/5 dark:bg-white/5"
                          }`}>
                            <span className={`font-semibold block ${
                              isUserMessage ? "chat-bubble-user-fg/80" : "text-orange-500"
                            }`}>
                              {getQuotedMessageSender(message.metadata.quoted_message_from)}
                            </span>
                            <span className="opacity-80 line-clamp-2">
                              {message.metadata.quoted_message_body}
                            </span>
                          </div>
                        )}

                        {/* Mídia indisponível (backlog cancelado ou falha terminal) */}
                        {(message.metadata as any)?.media_unavailable && (
                          <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-lg p-3 mb-2">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm">Mídia não disponível</span>
                          </div>
                        )}

                        {/* Imagem - com suporte a URL do Storage */}
                        {message.metadata?.type === "image" && !(message.metadata as any)?.media_unavailable && (
                          message.delivery_status === 'failed' && !message.metadata?.media?.url && !message.metadata?.media?.base64 ? (
                            <div
                              className="flex items-center gap-2 text-destructive bg-destructive/10 rounded-lg p-3 mb-2"
                              style={{ maxWidth: '320px' }}
                              title={(message.metadata as any)?.friendly_message || "Falha ao enviar imagem"}
                            >
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-sm">Falha ao enviar imagem</span>
                            </div>
                          ) : (message.metadata as any)?.media?.pending ? (
                            <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-lg p-4 mb-2" style={{ maxWidth: '320px' }}>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              <span className="text-sm">{getMediaPendingLabel(message, "Enviando imagem...")}</span>
                            </div>
                          ) : message.metadata?.media?.url ? (
                            <img
                              src={message.metadata.media.url}
                              alt="Imagem enviada"
                              className="rounded-lg mb-2 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                              style={{ maxWidth: '320px', maxHeight: '400px' }}
                              loading="lazy"
                              onError={(e) => {
                                if (message.metadata?.media?.base64) {
                                  e.currentTarget.src = `data:${message.metadata.media.mimetype || 'image/jpeg'};base64,${message.metadata.media.base64}`;
                                  return;
                                }
                                const img = e.currentTarget;
                                if (img.dataset.refreshedMinioUrl === "true") return;
                                img.dataset.refreshedMinioUrl = "true";
                                refreshMinioMediaUrl(message).then((nextUrl) => {
                                  if (nextUrl) img.src = nextUrl;
                                });
                              }}
                              onClick={(e) => setExpandedImage((e.currentTarget as HTMLImageElement).src)}
                            />
                          ) : message.metadata?.media?.base64 ? (
                            <img
                              src={`data:${message.metadata.media.mimetype || 'image/jpeg'};base64,${message.metadata.media.base64}`}
                              alt="Imagem recebida"
                              className="rounded-lg mb-2 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                              style={{ maxWidth: '320px', maxHeight: '400px' }}
                              onClick={(e) => setExpandedImage((e.currentTarget as HTMLImageElement).src)}
                            />
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-lg p-3">
                              <Image className="h-5 w-5" />
                              <span className="text-sm">[Imagem não disponível]</span>
                            </div>
                          )
                        )}

                        {/* Áudio */}
                        {/* Áudio fallback - sem mídia */}
                        {message.metadata?.type === "audio" && !message.metadata?.media?.base64 && !message.metadata?.media?.url && !(message.metadata as any)?.media_unavailable && (
                          message.delivery_status === 'failed' ? (
                            <div
                              className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 mb-2 max-w-[300px]"
                              title={(message.metadata as any)?.friendly_message || "Falha ao enviar áudio"}
                            >
                              <AlertCircle className="w-4 h-4" />
                              <p className="text-sm">Falha ao enviar áudio</p>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border mb-2 max-w-[300px]">
                              {(message.metadata as any)?.media?.pending ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">{getMediaPendingLabel(message, "Enviando áudio...")}</p>
                                </>
                              ) : (
                                <>
                                  <Mic className="w-5 h-5 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">🎤 Áudio (processando...)</p>
                                </>
                              )}
                            </div>
                          )
                        )}
                        {message.metadata?.type === "audio" && (message.metadata?.media?.base64 || message.metadata?.media?.url) && (() => {
                          const progress = audioPlayers[index]?.duration
                            ? (audioPlayers[index].currentTime / audioPlayers[index].duration)
                            : 0;
                          const barCount = 30;
                          const audioTranscription = getAudioTranscription(message);
                          const isTranscriptionExpanded = Boolean(
                            message.id && audioTranscriptionExpandedIds.has(message.id),
                          );
                          // Deterministic waveform from message id
                          const bars = Array.from({ length: barCount }, (_, i) => {
                            const hash = (message.id || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0) * (i + 1), i * 7);
                            return (hash % 20) + 8;
                          });
                          const currentRate = audioPlayers[index]?.playbackRate || 1;
                          const cycleSpeed = () => {
                            const speeds = [1, 1.5, 2];
                            const next = speeds[(speeds.indexOf(currentRate) + 1) % speeds.length] || 1;
                            setPlaybackRate(index, next);
                          };
                          return (
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <audio
                              ref={(el) => {
                                if (el) {
                                  audioRefs.current[index] = el;
                                  if (!audioPlayers[index]?.playbackRate) {
                                    el.playbackRate = 1;
                                  }
                                }
                              }}
                              src={message.metadata.media.url 
                                ? message.metadata.media.url 
                                : `data:${detectAudioMimetypeFromBase64(message.metadata.media.base64) || message.metadata.media.mimetype};base64,${message.metadata.media.base64}`}
                              onTimeUpdate={() => updateTime(index)}
                              onLoadedMetadata={() => {
                                updateTime(index);
                                const audio = audioRefs.current[index];
                                if (audio) {
                                  audio.playbackRate = audioPlayers[index]?.playbackRate || 1;
                                }
                              }}
                              onEnded={() => {
                                setAudioPlayers(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    playing: false,
                                    currentTime: 0,
                                    playbackRate: prev[index]?.playbackRate || 1
                                  }
                                }));
                              }}
                              onError={() => {
                                refreshMinioMediaUrl(message);
                              }}
                              className="hidden"
                              preload="metadata"
                            />
                            <div className={`rounded-2xl px-2 py-2 flex items-center gap-2 w-full min-w-0 ${message.role === "user"
                              ? "chat-bubble-user/20"
                              : "bg-gray-100 dark:bg-gray-800"
                              }`}>
                              {/* Play/Pause */}
                              <button
                                onClick={() => toggleAudio(index)}
                                className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${message.role === "user"
                                  ? "bg-primary-foreground/20 hover:bg-primary-foreground/30 chat-bubble-user-fg"
                                  : "bg-gray-700 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-500 text-white"
                                  }`}
                                type="button"
                              >
                                {audioPlayers[index]?.playing ? (
                                  <Pause className="w-4 h-4" />
                                ) : (
                                  <Play className="w-4 h-4 ml-0.5" />
                                )}
                              </button>

                              {/* Waveform */}
                              <div
                                className="flex-1 flex items-center gap-[2px] h-8 cursor-pointer"
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                                  const audio = audioRefs.current[index];
                                  if (audio && audio.duration) {
                                    audio.currentTime = ratio * audio.duration;
                                    updateTime(index);
                                  }
                                }}
                              >
                                {bars.map((h, i) => {
                                  const filled = i / barCount < progress;
                                  return (
                                    <div
                                      key={i}
                                      className={`rounded-full transition-colors ${
                                        filled
                                          ? message.role === "user"
                                            ? "bg-primary-foreground/80"
                                            : "bg-gray-700 dark:bg-gray-300"
                                          : message.role === "user"
                                            ? "bg-primary-foreground/30"
                                            : "bg-gray-400 dark:bg-gray-600"
                                      }`}
                                      style={{ width: '3px', height: `${h}px`, flexShrink: 0 }}
                                    />
                                  );
                                })}
                              </div>

                              {/* Duration / current time */}
                              <span className={`text-[10px] font-medium flex-shrink-0 min-w-[32px] text-right ${message.role === "user" ? "chat-bubble-user-fg" : "text-gray-600 dark:text-gray-400"
                                }`}>
                                {audioPlayers[index]?.playing
                                  ? formatTime(audioPlayers[index]?.currentTime || 0)
                                  : formatTime(audioPlayers[index]?.duration || 0)}
                              </span>

                              {/* Speed toggle */}
                              {currentRate !== 1 || audioPlayers[index]?.playing ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); cycleSpeed(); }}
                                  className={`flex-shrink-0 text-[10px] font-bold rounded-full w-7 h-5 flex items-center justify-center ${
                                    message.role === "user"
                                      ? "bg-primary-foreground/20 chat-bubble-user-fg"
                                      : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                                  }`}
                                >
                                  {currentRate}x
                                </button>
                              ) : null}
                            </div>
                            {audioTranscription && isTranscriptionExpanded && (
                              <div
                                className={`mt-2 w-full min-w-0 max-w-full border-t pt-2 [inline-size:100%] ${
                                  message.role === "user"
                                    ? "border-primary-foreground/15"
                                    : "border-border/60"
                                }`}
                              >
                                <p
                                  className={`block w-full max-w-full min-w-0 text-xs sm:text-sm whitespace-normal break-words [overflow-wrap:anywhere] leading-relaxed ${
                                    message.role === "user"
                                      ? "chat-bubble-user-fg/90"
                                      : "text-muted-foreground"
                                  }`}
                                >
                                  {audioTranscription}
                                </p>
                              </div>
                            )}
                          </div>
                          );
                        })()}

                        {/* Vídeo */}
                        {message.metadata?.type === "video" && (message.metadata?.media?.base64 || message.metadata?.media?.url) && (
                          <video controls className="max-w-full rounded-lg mb-2 max-h-64">
                            <source
                              src={message.metadata.media.base64 
                                ? `data:${message.metadata.media.mimetype};base64,${message.metadata.media.base64}`
                                : message.metadata.media.url}
                              type={message.metadata.media.mimetype}
                            />
                          </video>
                        )}
                        {message.metadata?.type === "video" && !message.metadata?.media?.base64 && !message.metadata?.media?.url && (
                          message.delivery_status === 'failed' ? (
                            <div
                              className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 mb-2 max-w-[300px]"
                              title={(message.metadata as any)?.friendly_message || "Falha ao enviar vídeo"}
                            >
                              <AlertCircle className="w-4 h-4" />
                              <p className="text-sm">Falha ao enviar vídeo</p>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border mb-2 max-w-[300px]">
                              {(message.metadata as any)?.media?.pending ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">{getMediaPendingLabel(message, "Enviando vídeo...")}</p>
                                </>
                              ) : (
                                <>
                                  <Play className="w-5 h-5 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">Vídeo</p>
                                </>
                              )}
                            </div>
                          )
                        )}

                        {/* Conteúdo Compartilhado (Reels, Posts, Stories) */}
                        {message.metadata?.type === "share" && (
                          <div className="flex flex-col rounded-lg bg-muted/50 border border-muted mb-2 max-w-[300px] overflow-hidden">
                            <div className="flex items-center gap-3 p-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                                <Instagram className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">Conteúdo Compartilhado</p>
                                <p className="text-xs text-muted-foreground">Reels, Post ou Story</p>
                              </div>
                            </div>
                            {(message.metadata as Record<string, unknown>)?.share_url && (
                              <a
                                href={String((message.metadata as Record<string, unknown>).share_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 px-3 py-2 border-t border-muted text-xs font-medium text-primary hover:bg-muted/80 transition-colors"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Abrir no Instagram
                              </a>
                            )}
                          </div>
                        )}

                        {/* Menção no Story */}
                        {message.metadata?.type === "story_mention" && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-muted mb-2 max-w-[300px]">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 flex items-center justify-center">
                              <Instagram className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">Menção no Story</p>
                              <p className="text-xs text-muted-foreground">Você foi mencionado em um story</p>
                            </div>
                            {message.metadata?.media?.url && (
                              <a 
                                href={message.metadata.media.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={isUserMessage ? "chat-bubble-user-fg hover:underline text-xs" : "text-primary hover:underline text-xs"}
                              >
                                Abrir ↗
                              </a>
                            )}
                          </div>
                        )}

                        {/* Sticker (WhatsApp Cloud inbound) */}
                        {message.metadata?.type === "sticker" && !(message.metadata as any)?.media_unavailable && (
                          (message.metadata as any)?.media?.pending ? (
                            <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-lg p-3 mb-2" style={{ maxWidth: '200px' }}>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              <span className="text-sm">{getMediaPendingLabel(message, "Enviando sticker...")}</span>
                            </div>
                          ) : message.metadata?.media?.url ? (
                            <img
                              src={message.metadata.media.url}
                              alt="Sticker"
                              className="mb-2 object-contain"
                              style={{ maxWidth: '160px', maxHeight: '160px' }}
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-lg p-3 mb-2">
                              <span className="text-sm">[Sticker]</span>
                            </div>
                          )
                        )}

                        {/* Documento */}
                        {message.metadata?.type === "document" && (
                          <DocumentMessageBubble
                            messageId={message.id}
                            metadata={message.metadata as any}
                            isUserMessage={isUserMessage}
                          />
                        )}


                        {/* Texto - ocultar placeholders de imagem/vídeo/share; renderizar caption real quando houver */}
                        {(() => {
                          const meta: any = message.metadata || {};
                          const captionFromMeta: string =
                            meta.caption || meta?.media?.caption || "";

                          const isImagePlaceholder = isImagePlaceholderContent(
                            message.content,
                            meta.type,
                          );
                          const isVideoPlaceholder = isVideoPlaceholderContent(
                            message.content,
                            meta.type,
                          );
                          const isSharePlaceholder = isSharePlaceholderContent(
                            message.content,
                            meta.type,
                          );

                          const isStoryMention =
                            meta?.original_attachment_type === "story_mention" ||
                            meta?.type === "story_mention" ||
                            (message.content || "").trim() === "[Menção no Story]";

                          const isStoryPlaceholder =
                            meta.type === "story_mention" && (
                              !message.content ||
                              message.content === "[Menção no Story]"
                            );

                          const isDocPlaceholder =
                            meta.type === "document" && (
                              !message.content ||
                              message.content === "[Documento enviado]" ||
                              message.content === "[Documento]"
                            );

                          const shareCaption = resolveShareCaption({
                            content: message.content,
                            metadata: meta,
                          });
                          if (meta.type === "share" && shareCaption) {
                            return wrapInlineMessageText(<ExpandableMessageText text={shareCaption} />);
                          }

                          const mediaCaption = resolveMediaCaption({
                            content: message.content,
                            metadata: meta,
                          });
                          if (
                            (meta.type === "image" || meta.type === "video") &&
                            (isImagePlaceholder || isVideoPlaceholder) &&
                            mediaCaption
                          ) {
                            return wrapInlineMessageText(<ExpandableMessageText text={mediaCaption} />);
                          }

                          // Menção em Story: substituir body cru por linha discreta em itálico
                          if (isStoryMention) {
                            const caption = captionFromMeta;
                            const isOutbound =
                              meta?.mention_direction === "outbound" ||
                              (meta?.mention_direction !== "inbound" && message.role === "user");
                            const label = isOutbound
                              ? "Você mencionou no Story"
                              : "Mencionou você no Story";
                            return wrapInlineMessageText(
                              <>
                                <p className="text-xs italic opacity-80">{label}</p>
                                {caption && (
                                  <ExpandableMessageText text={caption} className="mt-1" />
                                )}
                              </>,
                            );
                          }

                          // Enquete inbound não suportada (cliente enviou pelo app WhatsApp)
                          if (isUnsupportedInboundPoll(meta)) {
                            return wrapInlineMessageText(
                              <div className="space-y-1">
                                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                <p className="text-xs opacity-70">
                                  A API Cloud da Meta não entrega o conteúdo de enquetes. Peça ao
                                  contato para responder em texto.
                                </p>
                              </div>,
                            );
                          }

                          // Resposta inbound a botões/lista/enquete (WhatsApp Cloud)
                          const inboundInteractiveReply = parseInboundInteractiveReply(meta);
                          if (inboundInteractiveReply) {
                            const quotedWamid =
                              meta.quoted_message_id ||
                              (meta.context as Record<string, unknown> | undefined)?.id;
                            const quotedMsg = quotedWamid
                              ? currentMessages.find(
                                  (m) =>
                                    m.provider_message_id === quotedWamid ||
                                    (m.metadata as Record<string, unknown> | undefined)?.wamid ===
                                      quotedWamid,
                                )
                              : undefined;
                            const isPollVote = !!parseFabPoll(
                              quotedMsg?.metadata as Record<string, unknown> | undefined,
                            );

                            return (
                              <WhatsAppInteractiveReplyBubble
                                title={inboundInteractiveReply.title}
                                description={inboundInteractiveReply.description}
                                kind={inboundInteractiveReply.kind}
                                isPollVote={isPollVote}
                                isOutgoing={isUserMessage}
                              />
                            );
                          }

                          // Carrossel outbound (UAZAPI)
                          const outboundCarousel = parseOutboundCarouselDisplay(meta);
                          if (outboundCarousel) {
                            return (
                              <WhatsAppCarouselMessageBubble
                                carousel={outboundCarousel}
                                variant={isUserMessage ? "outgoing" : "incoming"}
                              />
                            );
                          }

                          // Enquete outbound (lista interativa com fab_poll)
                          const outboundPoll = parseOutboundPollDisplay(meta);
                          if (outboundPoll) {
                            return (
                              <WhatsAppPollMessageBubble
                                poll={outboundPoll}
                                variant={isUserMessage ? "outgoing" : "incoming"}
                              />
                            );
                          }

                          // Mensagem interactive outbound (botões/lista)
                          const outboundInteractive =
                            parseOutboundInteractive(meta) ??
                            (isInteractivePlaceholderContent(message.content, meta)
                              ? parseInteractivePreviewFromBody(message.content)
                              : null);
                          if (outboundInteractive) {
                            return (
                              <WhatsAppInteractiveMessageBubble
                                interactive={outboundInteractive}
                                variant={isUserMessage ? "outgoing" : "incoming"}
                              />
                            );
                          }

                          // Pedido de catálogo (OrderMessage)
                          const catalogOrder = parseCatalogOrderDisplay(
                            meta as Record<string, unknown>,
                            message.content,
                          );
                          if (catalogOrder) {
                            return (
                              <WhatsAppCatalogOrderBubble
                                order={catalogOrder}
                                variant={isUserMessage ? "outgoing" : "incoming"}
                              />
                            );
                          }

                          // Contato compartilhado (vCard)
                          if (
                            (meta.type === "contacts" || meta.type === "contact") &&
                            Array.isArray((meta as any).contacts) &&
                            (meta as any).contacts.length > 0
                          ) {
                            return (
                              <SharedContactBubble
                                metadata={meta}
                                workspaceId={workspaceId}
                                integrationId={activeConversation?.integration_id ?? null}
                                isUserMessage={isUserMessage}
                                fallbackText={message.content}
                              />
                            );
                          }


                          // Outros placeholders ocultos
                          if (
                            meta.type === "audio" ||
                            isSharePlaceholder ||
                            isStoryPlaceholder ||
                            isDocPlaceholder ||
                            isImagePlaceholder ||
                            isVideoPlaceholder
                          ) {
                            return null;
                          }

                          const displayContent = stripInteractivePreviewFallback(message.content);

                          // Body normal
                          return wrapInlineMessageText(
                            <p
                              className="whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(displayContent) }}
                            />,
                          );
                        })()}

                        {/* Indicador de Lembrete de Agendamento */}
                        {message.metadata?.type === "appointment_reminder" && (
                          <div className={`flex items-center gap-2 text-xs mt-2 p-2 rounded border ${
                            leadMetadata?.appointment_confirmed 
                              ? "bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300" 
                              : "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300"
                          }`}>
                            {leadMetadata?.appointment_confirmed ? (
                              <>
                                <CheckCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="font-medium">✓ Agendamento Confirmado</span>
                              </>
                            ) : (
                              <>
                                <span className="text-lg">⏳</span>
                                <span className="font-medium">Aguardando confirmação</span>
                              </>
                            )}
                          </div>
                        )}

                        {(conv.channel !== "whatsapp" || messageTextLayout !== "inline") &&
                          renderMessageTimestamp("block")}
                        </>)}
                      </div>

                      {/* Exibir reações existentes */}
                      {!message.deleted_at && message.reactions && message.reactions.length > 0 && (
                        <div className={`flex gap-1 ${isUserMessage ? "justify-end" : "justify-start"}`}>
                          {message.reactions.map((r, i) => (
                            <span
                              key={i}
                              className="text-base leading-none bg-background/80 rounded-full px-1.5 py-0.5 shadow-sm border [font-family:'Apple_Color_Emoji','Segoe_UI_Emoji','Noto_Color_Emoji',sans-serif]"
                              title={r.from_user === "assistant" ? "Você" : leadName}
                            >
                              {normalizeReactionEmoji(r.reaction)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {isUserMessage && !selectionMode && (
                      <Avatar className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0">
                        {accountProfilePicture ? (
                          <AvatarImage src={accountProfilePicture} alt="Conta" />
                        ) : null}
                        <AvatarFallback className="chat-bubble-user">
                          {isManualPhoneMessage ? (
                            <Smartphone className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden />
                          ) : (
                            <Bot className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden />
                          )}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                  
                  {/* Logs de atividade (IA ativada/desativada) entre mensagens */}
                  {logsBetween.map((log) => renderActivityLogPill(log))}
                </React.Fragment>
                );
              })}
              {/* Logs ocorridos APÓS a última mensagem (toggles em conversas paradas, estados iniciais, etc.) */}
              {(() => {
                const lastMsg = currentMessages[currentMessages.length - 1];
                const lastTime = lastMsg?.created_at ? new Date(lastMsg.created_at).getTime() : 0;
                const logsAfterLast = activityLogs.filter(log => {
                  const t = new Date(log.created_at).getTime();
                  return t >= lastTime;
                });
                return logsAfterLast.map((log) => renderActivityLogPill(log));
              })()}
              <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Barra de seleção */}
            {selectionMode && (
              <div className="flex items-center justify-between p-2 sm:p-3 border-t bg-muted/50">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedMessageIds.size} selecionada{selectedMessageIds.size !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectionMode(false);
                      setSelectedMessageIds(new Set());
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedMessageIds.size === 0}
                    onClick={() => handleDeleteMessages([...selectedMessageIds])}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Apagar ({selectedMessageIds.size})
                  </Button>
                </div>
              </div>
            )}

            {/* Input de envio */}
            <div className="flex-shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*,video/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />

              <form
                ref={composerFormMeasureRef}
                onSubmit={handleSubmit}
                className={cn(
                  "min-w-0 border-t bg-background",
                  isMobile ? "space-y-1.5 px-1.5 pt-1.5" : "space-y-2 p-2 sm:p-3 lg:p-4",
                )}
                style={
                  isMobile
                    ? {
                        paddingBottom:
                          "calc(max(0.75rem, env(safe-area-inset-bottom)) + var(--chat-keyboard-offset, 0px))",
                      }
                    : undefined
                }
              >
                {!isMobile && (
                <div className="text-[11px] text-muted-foreground">
                  Você está respondendo como: <span className="font-medium">{accountOriginLabel || activeConversation?.originLabel || activeChannel}</span>
                </div>
                )}
                {activePendingSendCount > 0 && (
                  <div className="flex items-center gap-1.5 px-0.5 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3 shrink-0 animate-pulse" />
                    <span>
                      {activePendingSendCount === 1
                        ? "Enviando mensagem..."
                        : `Enviando ${activePendingSendCount} mensagens...`}
                    </span>
                  </div>
                )}
                {/* Sugestão de melhoria da mensagem */}
                {enhancedText && (
                  <div className="flex flex-col gap-2 p-3 bg-muted/60 border border-border rounded-lg">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Sugestão de melhoria</span>
                    </div>
                    <p className="text-sm max-h-32 overflow-y-auto whitespace-pre-wrap">{enhancedText}</p>
                    <div className="flex items-center gap-2 justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={handleEnhanceMessage}
                        disabled={isEnhancing}
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${isEnhancing ? "animate-spin" : ""}`} />
                        Regenerar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setEnhancedText(null)}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setInput(enhancedText);
                          setEnhancedText(null);
                        }}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Usar
                      </Button>
                    </div>
                  </div>
                )}
                {/* Preview da mensagem citada */}
                {quotedMessage && (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 border-l-4 border-orange-400 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-orange-500 block">
                        Respondendo a {quotedMessage.from === "assistant" ? "Você" : leadName}
                      </span>
                      <span className="text-sm text-muted-foreground truncate block">
                        {quotedMessage.content}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={() => setQuotedMessage(null)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {selectedFile && recordingState === 'idle' && (
                  <div className="flex items-center gap-2 p-2 sm:p-3 bg-muted rounded-lg">
                    {previewUrl && selectedFile.type.startsWith('video/') ? (
                      <video src={previewUrl} className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded" muted />
                    ) : previewUrl ? (
                      <img src={previewUrl} alt="Preview" className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded" />
                    ) : selectedFile.type.startsWith('audio/') ? (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded bg-primary/10 flex items-center justify-center">
                        <Mic className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                      </div>
                    ) : getMediaType(selectedFile) === 'document' ? (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-red-600 dark:text-red-400" />
                      </div>
                    ) : (
                      <Paperclip className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {selectedFile.type.startsWith('audio/') ? 'Áudio gravado' : selectedFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedFile.type.startsWith('audio/')
                          ? `${formatTime(recordedDuration || 0)} • ${(selectedFile.size / 1024).toFixed(0)} KB`
                          : `${(selectedFile.size / 1024).toFixed(0)} KB`
                        }
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                        setRecordingTime(0);
                        setRecordedDuration(0);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* WhatsApp-style recording bar */}
                {recordingState === 'recording' ? (
                  <div className="flex gap-2 items-center px-2 py-2 bg-muted rounded-lg">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={cancelRecording}
                      className="h-9 w-9 flex-shrink-0 text-destructive hover:text-destructive"
                      title="Cancelar gravação"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>

                    <div className="flex items-center gap-2 flex-1">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive"></span>
                      </span>
                      <span className="text-sm font-mono font-medium text-foreground min-w-[3ch]">
                        {formatTime(recordingTime)}
                      </span>
                      {/* Visual waveform dots */}
                      <div className="flex items-center gap-0.5 flex-1 overflow-hidden">
                        {[14,8,18,6,12,20,9,15,7,17,11,19,8,13,16,10,18,7,14,20,9,12,16,8,15,11,19,7,13,17].map((h, i) => (
                          <div
                            key={i}
                            className="w-1 rounded-full bg-muted-foreground/40"
                            style={{
                              height: `${h}px`,
                              animation: `pulse ${0.5 + (i % 5) * 0.2}s ease-in-out infinite alternate`,
                              animationDelay: `${i * 0.05}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={stopRecording}
                      className="h-9 w-9 flex-shrink-0"
                      title="Parar gravação"
                    >
                      <Square className="w-4 h-4 fill-current" />
                    </Button>

                    <Button
                      type="button"
                      size="icon"
                      onClick={() => { stopRecording(); }}
                      className="h-9 w-9 flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                      title="Parar e enviar"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                ) : recordingState === 'preview' ? (
                  <div className="flex gap-2 items-center px-2 py-2 bg-muted rounded-lg">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={cancelRecording}
                      className="h-9 w-9 flex-shrink-0 text-destructive hover:text-destructive"
                      title="Descartar áudio"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>

                    <div className="flex items-center gap-2 flex-1">
                      {audioPreviewUrl && (
                        <audio
                          controls
                          src={audioPreviewUrl}
                          className="h-8 flex-1 max-w-full"
                          style={{ minWidth: 0 }}
                        />
                      )}
                      <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {formatTime(recordedDuration)}
                      </span>
                    </div>

                    <Button
                      type="button"
                      size="icon"
                      onClick={sendRecordedAudio}
                      disabled={isLoading}
                      className="h-9 w-9 flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                      title="Enviar áudio"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <LayoutsPopover
                      ref={layoutsPopoverRef}
                      showTrigger={false}
                      workspaceId={workspaceId}
                      onInsertText={(text) => setInput(text)}
                      onSendFile={(file) => {
                        setSelectedFile(file);
                        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
                          setPreviewUrl(URL.createObjectURL(file));
                        }
                      }}
                      disabled={isLoading || conv.state === "closed"}
                    />

                    {/* Compositor: mobile minimalista; desktop adapta largura via ResizeObserver */}
                    {isMobile ? (
                    <div className="flex w-full min-w-0 items-center gap-1.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                            aria-label="Anexar ou mais opções"
                            disabled={isLoading || conv.state === "closed"}
                          >
                            <Plus className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" side="top" className="z-[60] w-56">
                          {renderComposerOverflowItems(conv)}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div className="flex min-h-10 min-w-0 flex-1 items-center rounded-full border border-input bg-muted/50 px-1 py-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setMobileEmojiDialogOpen(true)}
                          disabled={conv.state === "active" || conv.state === "closed"}
                          title={conv.state === "active" ? "Pause o agente para inserir emoji" : "Inserir emoji"}
                          className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                        >
                          <Smile className="h-4 w-4" />
                        </Button>
                        <Textarea
                          ref={mobileComposerTextareaRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onPaste={(e) => handleComposerPaste(e, conv)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (!input.trim() && !selectedFile) return;
                              if (selectedFile) {
                                void handleSendMedia();
                                return;
                              }
                              e.currentTarget.form?.requestSubmit();
                            }
                          }}
                          placeholder={
                            selectedFile
                              ? "Legenda (opcional)..."
                              : conv.state === "paused"
                                ? "Mensagem"
                                : conv.state === "closed"
                                  ? "Conversa encerrada"
                                  : "Pause o agente para responder..."
                          }
                          disabled={conv.state === "active" || conv.state === "closed"}
                          rows={1}
                          className="my-auto min-h-0 max-h-[84px] min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-0 text-base leading-5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </div>

                      {input.trim() || selectedFile ? (
                        <Button
                          type="submit"
                          disabled={selectedFile ? isLoading : !input.trim()}
                          onClick={
                            selectedFile
                              ? (e) => {
                                  e.preventDefault();
                                  handleSendMedia();
                                }
                              : undefined
                          }
                          className="relative h-9 w-9 shrink-0 rounded-full p-0"
                          aria-label={activePendingSendCount > 0 ? `Enviar (${activePendingSendCount} na fila)` : "Enviar"}
                          title={activePendingSendCount > 0 ? `${activePendingSendCount} mensagem(ns) na fila` : "Enviar"}
                        >
                          <Send className="h-4 w-4" />
                          {activePendingSendCount > 0 && (
                            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-foreground px-0.5 text-[10px] font-semibold text-primary shadow-sm">
                              {activePendingSendCount > 9 ? "9+" : activePendingSendCount}
                            </span>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => cameraInputRef.current?.click()}
                            disabled={
                              isLoading ||
                              conv.state === "active" ||
                              conv.state === "closed" ||
                              (conv.channel === "instagram" && conv.state !== "paused")
                            }
                            title="Câmera ou galeria"
                            className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                          >
                            <Camera className="h-5 w-5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={startRecording}
                            disabled={
                              isLoading ||
                              conv.state === "active" ||
                              conv.state === "closed" ||
                              (conv.channel === "instagram" && conv.state !== "paused")
                            }
                            title={
                              conv.state === "active"
                                ? "Pause o agente para gravar áudio"
                                : "Gravar áudio"
                            }
                            className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                          >
                            <Mic className="h-5 w-5" />
                          </Button>
                        </>
                      )}
                    </div>
                    ) : useWideComposerToolbar ? (
                    <div className="flex w-full min-w-0 items-end gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            aria-label="Mais opções de mensagem"
                            disabled={isLoading || conv.state === "closed"}
                          >
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" side="top" className="z-[60] w-56">
                          {renderComposerOverflowItems(conv)}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={startRecording}
                        disabled={isLoading || conv.state === "active" || conv.state === "closed"}
                        title={
                          conv.state === "active"
                            ? "Pause o agente para gravar áudio"
                            : "Gravar áudio"
                        }
                        className="h-10 w-10 shrink-0"
                      >
                        <Mic className="h-4 w-4" />
                      </Button>

                      <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={isLoading || conv.state === "active" || conv.state === "closed"}
                            className="h-10 w-10 shrink-0"
                            title={conv.state === "active" ? "Pause o agente para inserir emoji" : "Inserir emoji"}
                          >
                            <Smile className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="fab-emoji-picker w-auto border-0 bg-transparent p-0 shadow-lg" side="top" align="start">
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            theme={emojiPickerTheme}
                            width={320}
                            height={350}
                            searchPlaceHolder="Buscar emoji..."
                            previewConfig={{ showPreview: false }}
                          />
                        </PopoverContent>
                      </Popover>

                      {conv.state === "paused" && input.trim().length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleEnhanceMessage}
                          disabled={isEnhancing}
                          title="Melhorar mensagem com IA"
                          className="h-10 w-10 shrink-0"
                        >
                          <Sparkles className={`h-4 w-4 ${isEnhancing ? "animate-spin" : ""}`} />
                        </Button>
                      )}

                      <Textarea
                        ref={desktopComposerTextareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPaste={(e) => handleComposerPaste(e, conv)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!input.trim() && !selectedFile) return;
                            if (selectedFile) {
                              void handleSendMedia();
                              return;
                            }
                            e.currentTarget.form?.requestSubmit();
                          }
                        }}
                        placeholder={
                          selectedFile
                            ? "Legenda (opcional)..."
                            : conv.state === "paused"
                              ? `Responder via ${conv.channel}...`
                              : conv.state === "closed"
                                ? "Conversa encerrada"
                                : "Pause o agente para responder..."
                        }
                        disabled={conv.state === "active" || conv.state === "closed"}
                        rows={1}
                        className="min-h-[40px] max-h-[84px] min-w-0 flex-1 resize-none border-input bg-background py-2.5 text-base leading-snug md:text-sm"
                      />

                      <Button
                        type="submit"
                        disabled={selectedFile ? isLoading : !input.trim()}
                        onClick={
                          selectedFile
                            ? (e) => {
                                e.preventDefault();
                                handleSendMedia();
                              }
                            : undefined
                        }
                        className="relative h-10 shrink-0 px-3 sm:px-4"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Enviar
                        {activePendingSendCount > 0 && (
                          <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground px-1 text-[11px] font-semibold text-primary">
                            {activePendingSendCount > 9 ? "9+" : activePendingSendCount}
                          </span>
                        )}
                      </Button>
                    </div>
                    ) : (
                    <div className="flex w-full min-w-0 items-end gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            aria-label="Mais opções de mensagem"
                            disabled={isLoading || conv.state === "closed"}
                          >
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" side="top" className="z-[60] w-52">
                          {renderComposerOverflowItems(conv)}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={startRecording}
                        disabled={
                          isLoading ||
                          conv.state === "active" ||
                          conv.state === "closed" ||
                          (conv.channel === "instagram" && conv.state !== "paused")
                        }
                        title={
                          conv.state === "active"
                            ? "Pause o agente para gravar áudio"
                            : "Gravar áudio"
                        }
                        className="h-10 w-10 shrink-0"
                      >
                        <Mic className="h-4 w-4" />
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setMobileEmojiDialogOpen(true)}
                        disabled={isLoading || conv.state === "active" || conv.state === "closed"}
                        title={conv.state === "active" ? "Pause o agente para inserir emoji" : "Inserir emoji"}
                        className="h-10 w-10 shrink-0"
                      >
                        <Smile className="h-4 w-4" />
                      </Button>

                      {conv.state === "paused" && input.trim().length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleEnhanceMessage}
                          disabled={isEnhancing}
                          title="Melhorar mensagem com IA"
                          className="h-10 w-10 shrink-0"
                        >
                          <Sparkles className={`h-4 w-4 ${isEnhancing ? "animate-spin" : ""}`} />
                        </Button>
                      )}

                      <Textarea
                        ref={mobileComposerTextareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPaste={(e) => handleComposerPaste(e, conv)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!input.trim() && !selectedFile) return;
                            if (selectedFile) {
                              void handleSendMedia();
                              return;
                            }
                            e.currentTarget.form?.requestSubmit();
                          }
                        }}
                        placeholder={
                          selectedFile
                            ? "Legenda (opcional)..."
                            : conv.state === "paused"
                              ? `Responder via ${conv.channel}...`
                              : conv.state === "closed"
                                ? "Conversa encerrada"
                                : "Pause o agente para responder..."
                        }
                        disabled={conv.state === "active" || conv.state === "closed"}
                        rows={1}
                        className="min-h-[40px] min-w-0 flex-1 resize-none border-input bg-background py-2.5 text-base leading-snug md:text-sm"
                      />

                      <Button
                        type="submit"
                        disabled={selectedFile ? isLoading : !input.trim()}
                        onClick={
                          selectedFile
                            ? (e) => {
                                e.preventDefault();
                                handleSendMedia();
                              }
                            : undefined
                        }
                        className="relative h-10 w-10 shrink-0 p-0"
                        aria-label={activePendingSendCount > 0 ? `Enviar (${activePendingSendCount} na fila)` : "Enviar"}
                        title={activePendingSendCount > 0 ? `${activePendingSendCount} mensagem(ns) na fila` : "Enviar"}
                      >
                        <Send className="h-4 w-4" />
                        {activePendingSendCount > 0 && !selectedFile && (
                          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-foreground px-0.5 text-[10px] font-semibold text-primary shadow-sm">
                            {activePendingSendCount > 9 ? "9+" : activePendingSendCount}
                          </span>
                        )}
                      </Button>
                    </div>
                    )}

                    <Dialog open={mobileEmojiDialogOpen} onOpenChange={setMobileEmojiDialogOpen}>
                      <DialogContent className="flex w-[calc(100vw-1.5rem)] max-h-[min(85dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1rem))] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
                        <DialogHeader className="shrink-0 border-b px-4 pb-2 pt-4 pr-12 text-left">
                          <DialogTitle className="text-base">Emojis</DialogTitle>
                        </DialogHeader>
                        <div className="fab-emoji-picker fab-emoji-picker--mobile min-h-0 flex-1 overflow-hidden px-1 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            theme={emojiPickerTheme}
                            width="100%"
                            height={mobileEmojiPickerHeight}
                            searchPlaceHolder="Buscar emoji..."
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </form>
            </div>
          </TabsContent>
          );
        })}
      </Tabs>

      <ChatProductLookupDialog
        open={productLookupOpen}
        onOpenChange={setProductLookupOpen}
        workspaceId={workspaceId}
        conversationId={activeConversation?.conversation_id ?? null}
        channel={activeChannel}
        whatsappProvider={
          activeConversation?.integration_id
            ? integrationProviders[activeConversation.integration_id]
            : undefined
        }
        onInsertToChat={(text) => {
          setInput(text);
          setEnhancedText(null);
        }}
      />

      {activeConversation?.integration_id &&
        supportsInteractiveOutbound(integrationProviders[activeConversation.integration_id]) && (
        <SendWhatsAppInteractiveDialog
          open={interactiveDialogOpen}
          onOpenChange={setInteractiveDialogOpen}
          conversationId={activeConversation.conversation_id}
          memberId={memberId}
          contextMessageId={quotedMessage?.messageId ?? null}
          provider={integrationProviders[activeConversation.integration_id]}
        />
      )}
      {activeConversation?.integration_id &&
        activeConversation.channel === "whatsapp" &&
        (integrationProviders[activeConversation.integration_id] === "whatsapp_cloud" ||
          integrationProviders[activeConversation.integration_id] === "whatsapp_uazapi") && (
        <SendWhatsAppContactDialog
          open={contactDialogOpen}
          onOpenChange={setContactDialogOpen}
          conversationId={activeConversation.conversation_id}
          workspaceId={workspaceId}
          memberId={memberId}
          contextMessageId={quotedMessage?.messageId ?? null}
          provider={integrationProviders[activeConversation.integration_id]}
        />
      )}
      <ForwardMessageDialog
        open={!!forwardMessage}
        onOpenChange={(open) => {
          if (!open) setForwardMessage(null);
        }}
        workspaceId={workspaceId}
        memberId={memberId}
        currentLeadId={leadId}
        sourceIntegrationId={activeConversation?.integration_id}
        sourceIntegrationLabel={
          activeConversation?.originLabel || accountOriginLabel || undefined
        }
        sourceMessage={
          forwardMessage?.id
            ? {
                id: forwardMessage.id,
                content: forwardMessage.content,
                metadata: forwardMessage.metadata as Record<string, unknown> | undefined,
              }
            : null
        }
      />
      {activeConversation?.integration_id && cloudIntegrationIds.has(activeConversation.integration_id) && (
        <>
          <SendWhatsAppTemplateDialog
            open={templateDialogOpen}
            onOpenChange={setTemplateDialogOpen}
            conversationId={activeConversation.conversation_id}
            integrationId={activeConversation.integration_id}
            leadId={leadId}
            leadName={leadName}
          />
        </>
      )}



      {/* Dialog para imagem expandida */}
      <Dialog open={!!expandedImage} onOpenChange={(open) => !open && setExpandedImage(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none flex items-center justify-center">
          <button
            onClick={() => setExpandedImage(null)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          {expandedImage && (
            <img 
              src={expandedImage} 
              alt="Imagem expandida"
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para editar mensagem */}
      <Dialog open={!!editingMessage} onOpenChange={(open) => { if (!open) setEditingMessage(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar mensagem</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            rows={4}
            autoFocus
            className="resize-none"
            placeholder="Digite a nova mensagem..."
          />
          <p className="text-xs text-muted-foreground">
            O WhatsApp permite editar apenas mensagens enviadas há menos de 15 minutos.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditingMessage(null)} disabled={isSavingEdit}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit || !editingText.trim()}>
              {isSavingEdit ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AttachMessageToTaskDialog
        open={!!attachToTaskMessage}
        onOpenChange={(open) => {
          if (!open) setAttachToTaskMessage(null);
        }}
        message={
          attachToTaskMessage?.id
            ? {
                id: attachToTaskMessage.id,
                content: attachToTaskMessage.content,
                role: attachToTaskMessage.role,
                created_at: attachToTaskMessage.created_at,
              }
            : null
        }
        workspaceId={workspaceId}
        memberId={memberId}
      />
    </div>
  );
};
